import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { SubmitHandler } from "react-hook-form";
import { TacticalBoardEditor } from "@/features/canvas/TacticalBoardEditor";
import { TacticalBoardThumbnail } from "@/features/canvas/TacticalBoardThumbnail";
import { TelestratorCanvas } from "@/features/canvas/TelestratorCanvas";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  emptyScene,
  sceneFromOnDisk,
  sceneToOnDisk,
  type BoardScene,
} from "@/lib/canvas/board-schema";
import {
  emptyDrawing,
  loadDrawing,
  saveDrawing,
  type DrawingV2,
} from "@/lib/canvas/telestrator-schema";
import { ApiError, errorMessage } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import type {
  CoachingClip,
  CoachingNote,
  CreateCoachingClipRequest,
  CreateCoachingNoteRequest,
  Match,
  UpdateCoachingClipRequest,
  UpdateCoachingNoteRequest,
} from "@/lib/types";
import {
  Composer,
  coerceNoteCategory,
  parseTagsRaw,
  type ComposerHandle,
  type ComposerValues,
} from "./components/Composer";
import {
  ClipComposer,
  MAX_CLIP_DURATION_SECONDS,
  type ClipComposerHandle,
  type ClipComposerValues,
} from "./components/ClipComposer";
import {
  FloatingToolPanel,
  FloatingToolToggle,
} from "./components/FloatingToolOverlay";
import {
  defaultSlotForMatch,
  isSlotValidForMatch,
} from "@/features/matches/matchSlots";
import { MatchSlotPicker, matchLabel } from "./components/MatchSlotPicker";
import { SourceToggle, type CoachReviewSource } from "./components/SourceToggle";
import { TemplateSelector } from "./components/TemplateSelector";
import { ToolRail } from "./components/ToolRail";
import { ClipTrack } from "./components/ClipTrack";
import { ClipPresetBar } from "./components/ClipPresetBar";
import { ThumbnailStrip } from "./components/ThumbnailStrip";
import { FrameStepper } from "./components/FrameStepper";
import { NotesAndClipsRail } from "./components/NotesAndClipsRail";
import { RecentObservationsRail } from "./components/RecentObservationsRail";
import { ComposerDrawer } from "./components/ComposerDrawer";
import { FilmIcon, PencilIcon, PlusIcon } from "./components/icons";
import { clamp, FRAME, type ClipWindow } from "./clipTrack";
import { useCoachReviewBundle } from "./hooks/useCoachReviewBundle";
import { useCoachNotes } from "./hooks/useCoachNotes";
import { useCoachClips } from "./hooks/useCoachClips";
import { useReviewKeyboardShortcuts } from "./hooks/useReviewKeyboardShortcuts";
import { useSaveCoachingNote } from "./hooks/useSaveCoachingNote";
import { useSaveCoachingClip } from "./hooks/useSaveCoachingClip";
import { useMatches } from "@/features/matches/useMatches";
import { useMe } from "@/features/auth/useMe";
import { getPublicNavCapabilities } from "@/features/navigation/navCapabilities";
import {
  formatPlayhead,
  useVideoPlayhead,
} from "@/features/playback/useVideoPlayhead";
import { useVideoKeyboardShortcuts } from "@/features/playback/useVideoKeyboardShortcuts";
import { FeedbackPlayerModal } from "@/features/playback/FeedbackPlayerModal";
import {
  CoachTabStrip,
  OPERATIONAL_COACH_TABS,
} from "@/features/coach/CoachTabStrip";
import { canAuthorCoachFeedback } from "@/features/coach/coachWorkspaceAccess";
import { TeamScopeBar } from "@/features/navigation/TeamScopeBar";
import { TeamScopeGate } from "@/features/navigation/TeamScopeGate";
import { useScopeStore } from "@/stores/scope";

/**
 * Coach Review — the unified shell for authoring video notes, video
 * clips, and tactical observations.
 *
 * Layout (M4) — mirrors the legacy Replay coach-review:
 *   - Header: title + bundle context + source toggle + focus-mode
 *     button + help button
 *   - Two columns at lg+: the LEFT column is the canvas stage
 *     (TacticalBoardEditor OR TelestratorCanvas, mode-switched by
 *     the source toggle); the RIGHT column is a single rail that
 *     stacks the canvas's built-in tool grid / color / stroke /
 *     formation controls AND, below them (passed via the canvas's
 *     `toolbarChildren` slot), the template selector + composer.
 *     The coach can type notes while watching the video / pitch.
 *   - Both surfaces get the SAME stage width (they differ only in
 *     height by aspect ratio) so the rail stays put on flip.
 *   - Focus mode (FocusModeOverlay below): the active canvas
 *     fills the viewport in a portaled overlay at <body>. The
 *     composer is hidden; tools are revealed on demand via a
 *     `<FloatingToolOverlay>` pill in the top-right corner.
 *     Mirrors legacy Replay focus-mode UX. Portal escape is
 *     load-bearing for the bounded stacking context the parent
 *     route's animation CSS creates.
 *   - Auth: M4 carries a localStorage Bearer-token placeholder.
 *     When the bundle 401s (or no token is stored), an inline
 *     `<InlineLoginPanel>` replaces the shell. Proper login UI
 *     lands in M9.
 *
 * Keyboard shortcuts: bound globally via useReviewKeyboardShortcuts.
 * Body-attribute scoping (SourceToggle sets/clears
 * `data-coach-review-source`) ensures shortcuts only fire when
 * Coach Review is the active route AND focus isn't in a form field.
 *
 * Save flow:
 *   - Composer submits its structured-text fields via onSubmit.
 *   - This component merges:
 *       - the form values (Zod-validated)
 *       - the canvas state (tactical_board_json OR drawing,
 *         depending on source)
 *       - the bundle's match context (match_id + null slot/
 *         timestamp for SP6; M5 wires HLS to provide the
 *         real timestamp_seconds)
 *   - Dispatches to useSaveCoachingNote (create OR update).
 *   - On success: invalidate the scoped cache (the hook does this)
 *     + reset the composer to fresh defaults (the SP5 key-bump
 *     pattern + clearing the template's lastAppliedId).
 *
 * Edit mode: when `note_id` is in the URL, the bundle returns the
 * note + auto-resolves its match. We pre-fill the composer +
 * canvas state from the seed and switch the save path to PATCH.
 */

export function CoachReview() {
  const search = useSearch({ from: "/coach/review" });
  // Route navigation — opening a clip from the NotesAndClipsRail
  // pushes `?clip_id=` so the bundle re-seeds in clip-edit mode.
  const navigate = useNavigate({ from: "/coach/review" });
  // Reconcile the auth + scope stores from `/api/me`. This route can
  // be navigated to directly (deep link, refresh, or straight from
  // login), and the scope store is NOT persisted — without this the
  // store's `teamId` stays null, `useMatches` (gated on `!!teamId`)
  // never fires, and the match picker is empty. `useMe` is a cached
  // query keyed `["me"]`, so calling it here dedupes with any other
  // caller — no extra round-trip.
  const me = useMe();
  // Multi-team users must pick a team before the scope-gated review
  // surface renders — see `<TeamScopeGate>`.
  const selectionRequired = useScopeStore((s) => s.selectionRequired);
  const bundle = useCoachReviewBundle({
    matchId: search.match_id,
    noteId: search.note_id,
    clipId: search.clip_id,
  });
  const saveNote = useSaveCoachingNote();
  const saveClip = useSaveCoachingClip();
  const toast = useToast();
  // Team-wide match list for the video-mode match picker (across
  // all the team's seasons — tktic#8).
  const matchesQuery = useMatches();

  // ---- Local canvas state ----
  // `source` seeds from the route's `?source=` intent (the M6a
  // creation-routing carry-in — a list page's "+ New observation"
  // routes here with `?source=tactical_board`). Absent ⇒ video,
  // the default authoring surface. Editing an existing note can
  // still auto-flip the source from the bundle (see the seed
  // effect below); the route intent is just the initial value.
  const [source, setSource] = useState<CoachReviewSource>(
    search.source ?? "video",
  );
  const [scene, setScene] = useState<BoardScene>(() => emptyScene());
  const [drawing, setDrawing] = useState<DrawingV2>(() => emptyDrawing());

  // ---- Video-mode match + slot anchor ----
  // A video coaching note is anchored to a match + slot (+ timestamp).
  // `matchId` seeds from the route's `?match_id=` when present (the
  // bundle resolved it); otherwise the coach picks one. `slot` seeds
  // to "full" but is corrected to the selected match's actual layout
  // by the effect below — a `two_halves` match has no `full` video.
  const [matchId, setMatchId] = useState<string>(search.match_id ?? "");
  const [slot, setSlot] = useState<string>("full");
  // Inline validation error when a video-mode save is attempted
  // without a match picked. Cleared on the next valid save.
  const [matchError, setMatchError] = useState<string | null>(null);
  // Set when the VOD heartbeat gets a 403 — the stream was killed
  // by an admin. The telestrator pauses the video itself; this
  // surfaces the "why" to the coach. Cleared when they pick a
  // different match (a fresh stream).
  const [streamKilled, setStreamKilled] = useState(false);
  // Keep matchId in sync if the route param changes (e.g. deep link).
  useEffect(() => {
    if (search.match_id) setMatchId(search.match_id);
  }, [search.match_id]);

  // The kill banner is keyed to the *current* match+slot — a fresh
  // stream gets a clean slate. The picker's `onMatchIdChange` also
  // clears it eagerly, but this effect covers the programmatic
  // paths the picker handler misses (a timeline-chip seek that
  // switches match, a deep-link route change). If the NEW match's
  // heartbeat also 403s, `useVodHeartbeat` re-raises the banner —
  // that's correct: a different killed stream is a new notice.
  useEffect(() => {
    setStreamKilled(false);
  }, [matchId, slot]);

  // ---- Composer mode: note vs. clip ----
  // Video mode hosts two composers — a coaching-NOTE composer and a
  // CLIP composer (a `[start, end]` window of the match video).
  // `composerMode` swaps which one the rail renders. Editing a clip
  // (`?clip_id=`) forces clip mode; editing a note (`?note_id=`)
  // forces note mode; otherwise the coach toggles. Tactical-board
  // mode has no clips — observations are notes only — so the toggle
  // is video-only and clip mode never applies there.
  const isClipEdit = search.clip_id !== undefined;
  const isNoteEdit = search.note_id !== undefined;
  const [composerMode, setComposerMode] = useState<"note" | "clip">(
    isClipEdit ? "clip" : "note",
  );
  // Keep the mode pinned to whichever edit the route demands.
  //
  // This runs on every `source` change too — not just on a route
  // change — because clip-edit must STAY clip mode. The tactical-
  // board source forces `note` mode (the board has no clips), and
  // the SourceToggle stays enabled under `?clip_id=`, so without
  // re-pinning a coach could go video → tactical-board → video and
  // land on the NOTE composer under a clip-edit URL — Save would
  // then write note data instead of updating the clip. Re-pinning
  // whenever the source is back on `video` closes that escape.
  useEffect(() => {
    if (source === "tactical_board") {
      // Observations are notes only — the board has no clip mode.
      setComposerMode("note");
      return;
    }
    // Video surface: the route param wins. Clip-edit pins clip
    // mode (including after a tactical-board round-trip);
    // note-edit pins note mode. A non-edit session leaves the
    // coach's manual toggle alone.
    if (isClipEdit) setComposerMode("clip");
    else if (isNoteEdit) setComposerMode("note");
  }, [isClipEdit, isNoteEdit, source]);

  // The full Match row for the picked id — needed by the
  // telestrator's `<HlsVideo>` (which builds stream URLs from
  // `match.team_id`). Resolve from the team-wide matches list
  // first; fall back to the bundle's resolved match (edit mode
  // hydrates the note's match there before the list lands).
  const selectedMatch = useMemo(
    () =>
      matchesQuery.data?.find((m) => m.id === matchId) ??
      bundle.data?.match ??
      undefined,
    [matchesQuery.data, matchId, bundle.data?.match],
  );

  // Keep `slot` consistent with the selected match's video layout.
  // The `slot` state seeds to "full", but a `two_halves` match has
  // no `full` video — leaving it there 404s the player. When a
  // match resolves whose layout doesn't include the current slot,
  // snap to that match's default slot. The guard (only correct an
  // INVALID slot) means a slot the coach deliberately picked, or
  // one seeded from a note/clip being edited, is never clobbered —
  // those are always valid for their own match.
  useEffect(() => {
    if (!selectedMatch) return;
    if (!isSlotValidForMatch(selectedMatch, slot)) {
      setSlot(defaultSlotForMatch(selectedMatch));
    }
  }, [selectedMatch, slot]);

  // ---- Stage size: single container-width budget ----
  //
  // `containerWidth` is the available width the canvas stage should
  // fill — the measured width of the stage `<section>`. Let the
  // measured width win even on narrow phones: `<main>` has horizontal
  // padding, so a 320px viewport can expose less than 320px of content.
  // Forcing a 320px inline stage there clips the right edge and hides
  // desktop-equivalent controls from mobile coaches.
  //
  // The section is marked `overflow-hidden` (see the JSX): the
  // canvas stage renders as a fixed-px, `flex-shrink-0` box at
  // `width={stageWidth}`, and without the clip an oversized stage
  // would inflate the section's own box, latching `containerWidth`
  // onto the largest size ever seen (a feedback loop that never
  // shrinks on resize). With `overflow-hidden` the section's box
  // stays exactly its flex-allocated width regardless of the
  // child, so observing it directly is stable.
  //
  // A CALLBACK ref (not `useRef` + a one-shot `useEffect`): the
  // section renders conditionally (behind the auth gate /
  // `selectionRequired` early-return). A `[]`-dep effect reading
  // `ref.current` would run once, find `null` if the section isn't
  // mounted yet, and never retry. The callback ref fires exactly
  // when the node attaches — same load-bearing pattern as
  // `useCanvasEnvelope`. `observe()` delivers an initial
  // measurement synchronously-ish, and every resize after.
  const [containerWidth, setContainerWidth] = useState(800);
  const stageMeasureObserver = useRef<ResizeObserver | null>(null);
  const stageMeasureRef = useCallback((node: HTMLElement | null) => {
    stageMeasureObserver.current?.disconnect();
    if (!node) {
      stageMeasureObserver.current = null;
      return;
    }
    const obs = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(Math.max(1, Math.floor(entry.contentRect.width)));
      }
    });
    obs.observe(node);
    stageMeasureObserver.current = obs;
  }, []);

  // ---- Focus mode ----
  // Declared early so the width budget below can read `focusOpen`.
  const [focusOpen, setFocusOpen] = useState(false);
  const focusButtonRef = useRef<HTMLButtonElement | null>(null);

  // ---- Composer drawer (v2) ----
  // The v2 layout moves the note / clip composer out of the canvas's
  // always-visible toolbar rail into a bottom slide-up drawer, opened
  // on demand by "Mark moment" / "Add details" (after "Make clip"
  // enters clip mode), and the M key. `composing`
  // is the open flag; the composer node mounts only while it's true (inline
  // mode) — closing unmounts it, which resets the form, matching the
  // standalone reference's "fresh form each open" behavior.
  //
  // Focus mode is unaffected: it still hosts the composer in its
  // floating tool panel (`composer={composerElement}` below). The
  // drawer is the INLINE surface only — opening focus mode closes
  // the drawer so the composer never mounts in two places at once.
  //
  // Edit mode (`?note_id=` / `?clip_id=`) is canvas-first: the coach
  // navigated here to see/edit the authored evidence. Details open
  // explicitly from the small Edit details action.
  const [composing, setComposing] = useState(false);
  const composeButtonRef = useRef<HTMLButtonElement | null>(null);

  // Edit deep links are canvas-first: the saved drawing / board scene
  // should be visible immediately, with details opened explicitly by
  // the "Edit details" action. Close must never tear down the canvas.

  // The stage fills the FULL width of its column.
  //
  // v1 subtracted a `CANVAS_TOOLBAR_BUDGET_PX` because the canvas
  // rendered its own 300px toolbar column inside this container. The
  // v2 restructure relocated the entire canvas toolbar into the
  // separate right-rail `<aside>` (via `toolRailSlot`), so the
  // canvas no longer has an in-container toolbar — subtracting the
  // budget would just leave a ~300px dead gap beside the video.
  //
  // `stageContainerRef`'s `<section>` is `flex-1` next to the 320px
  // aside, so `containerWidth` already IS the max width; the
  // `ResizeObserver` keeps it current, and `envelopeHeight` derives
  // 16:9 from it — so the stage stays max-width + aspect-correct on
  // every resize. Focus mode is the same (the overlay fills the
  // viewport).
  const stageWidth = useMemo(
    () => Math.max(1, containerWidth),
    [containerWidth],
  );

  // Both surfaces are now 16:9 and take the SAME width — the video
  // fills its 16:9 frame, and the tactical board is a 16:9 green
  // field with the pitch markings drawn inset at true proportions
  // (`TacticalBoardEditor` / `PitchBackground`). So a single
  // `stageWidth` drives both; no separate board sizing / letterbox.
  const videoStageWidth = stageWidth;
  const boardStageWidth = stageWidth;

  // ---- Seed scene/drawing/match anchor from bundle (edit mode) ----
  // Seed ONCE per edit target. The dep is the bundle note's object
  // IDENTITY, which changes on every refetch (staleTime expiry,
  // invalidateScope from a save) — re-running the seed then stomps
  // in-progress edit state: a coach who flipped the source toggle or
  // sketched on the board mid-edit got snapped back to the saved
  // note's surface/content (#360 surfaced this as a load-dependent
  // E2E flake). Same key pattern as `autoOpenedNoteKeyRef` below.
  const seededNoteKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!bundle.data?.note) {
      // Left edit mode (create-mode bundles carry no note) — drop
      // the key so RETURNING to the same note re-seeds.
      seededNoteKeyRef.current = null;
      return;
    }
    const note = bundle.data.note;
    const seedKey = String(note.id);
    if (seededNoteKeyRef.current === seedKey) return;
    seededNoteKeyRef.current = seedKey;

    if (note.note_context === "observation") {
      setSource("tactical_board");
      setComposerMode("note");
      // `tactical_board_json` is the on-disk shape (`stroke_width`).
      // `sceneFromOnDisk` converts it to the in-memory shape
      // (`strokeWidth`) the canvas + `sceneToOnDisk` round-trip
      // expects. Fall back to an empty scene if the blob is invalid.
      const parsed = note.tactical_board_json
        ? sceneFromOnDisk(note.tactical_board_json)
        : { ok: true as const, value: emptyScene() };
      setScene(parsed.ok ? parsed.value : emptyScene());
      setDrawing(emptyDrawing());
      return;
    }

    if (note.drawing && Object.keys(note.drawing).length > 0) {
      // `note.drawing` is the backend's on-disk shape (`type`-keyed).
      // `loadDrawing` converts it to the canonical in-memory shape
      // (`kind`-keyed) the canvas + `saveDrawing` round-trip expects.
      setDrawing(loadDrawing(note.drawing));
    } else {
      setDrawing(emptyDrawing());
    }
    // Editing a video note → seed the match + slot anchor so the
    // picker opens on the note's existing match/slot.
    if (note.note_context === "video") {
      setSource("video");
      setComposerMode("note");
      if (note.match_id) setMatchId(String(note.match_id));
      if (note.slot) setSlot(String(note.slot));
    }
  }, [bundle.data?.note]);

  // ---- Seed match anchor + drawing from bundle (clip edit mode) ----
  // Editing a clip (`?clip_id=`) — seed the match/slot picker on the
  // clip's anchor, and restore its drawing snapshot onto the canvas.
  // The clip's match/slot are immutable, so the picker is also
  // locked in clip-edit mode (see `MatchSlotPicker disabled` below).
  // Seed ONCE per clip — same refetch-stomp guard as the note seed.
  const seededClipKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const clip = bundle.data?.clip;
    if (!clip) {
      seededClipKeyRef.current = null;
      return;
    }
    const seedKey = String(clip.id);
    if (seededClipKeyRef.current === seedKey) return;
    seededClipKeyRef.current = seedKey;
    setSource("video");
    setComposerMode("clip");
    if (clip.match_id) setMatchId(String(clip.match_id));
    if (clip.slot) setSlot(String(clip.slot));
    if (clip.drawing && Object.keys(clip.drawing).length > 0) {
      // On-disk → in-memory, same as the note seed above.
      setDrawing(loadDrawing(clip.drawing));
    } else {
      setDrawing(emptyDrawing());
    }
  }, [bundle.data?.clip]);

  // ---- Composer default values ----
  const composerDefaults = useMemo<Partial<ComposerValues> | undefined>(() => {
    const note = bundle.data?.note;
    if (!note) {
      // Fresh mount (no edit-mode note). The M6a creation-routing
      // carry-in passes `?player_id=` so a "+ New observation"
      // routed from a Roster row opens with that player
      // preselected. Only that one field — everything else is the
      // composer's own DEFAULTS.
      if (search.player_id) {
        return { player_ids: [search.player_id] };
      }
      return undefined;
    }
    return {
      title: note.title,
      note_type: note.note_type,
      // The API note payload types `category` as a free string;
      // narrow it to the composer's closed enum (off-list → "other",
      // matching the backend default).
      category: coerceNoteCategory(note.category),
      visibility: note.visibility,
      body: note.body ?? "",
      player_summary: note.player_summary ?? "",
      what_happened: note.what_happened ?? "",
      why_it_matters: note.why_it_matters ?? "",
      what_to_do_next: note.what_to_do_next ?? "",
      coach_private_note: note.coach_private_note ?? "",
      tags_raw: (note.tags ?? []).join(", "),
      player_ids: note.player_ids ?? [],
      // Observation event metadata — empty for video notes.
      event_title: note.event_title ?? "",
      event_date: note.event_date ?? "",
      event_type: note.event_type ?? "practice",
    };
  }, [bundle.data?.note, search.player_id]);

  // Template-apply uses a key-bump on the composer so RHF picks up
  // the new defaults via the reset effect inside Composer.
  const [composerKey, setComposerKey] = useState(0);
  const [composerOverride, setComposerOverride] = useState<
    Partial<ComposerValues> | undefined
  >(undefined);

  // Imperative handle into the composer's RHF state. Two callers
  // need the live values (not the parent's stale defaults):
  //   - TemplateSelector's overwrite-protect check
  //   - the focus-mode portal transition, which snapshots the form
  //     before remounting the Composer inside the drawer
  // See ComposerHandle docstring for the full rationale.
  const composerRef = useRef<ComposerHandle | null>(null);
  const getLiveComposerValues = (): Partial<ComposerValues> =>
    composerRef.current?.getValues() ?? composerOverride ?? composerDefaults ?? {};

  // ---- Clip composer state ----
  const clipComposerRef = useRef<ClipComposerHandle | null>(null);
  // Key-bump remounts the clip composer to re-seed defaults (after a
  // save, or on the focus-mode portal transition — see the focus
  // handlers).
  const [clipComposerKey, setClipComposerKey] = useState(0);
  // Override that survives a key-bump remount — the snapshot of
  // live clip-composer values taken before the focus-mode portal
  // transition, mirroring the note composer's `composerOverride`.
  // `undefined` means "use the computed defaults".
  const [clipComposerOverride, setClipComposerOverride] = useState<
    Partial<ClipComposerValues> | undefined
  >(undefined);
  // Frozen create-mode window. The playhead-derived `[t-5, t+8]`
  // window is captured into STATE once (on entering clip-create
  // mode, and after a save), NOT recomputed every render — a
  // per-tick memo would hand `ClipComposer` a fresh `defaultValues`
  // object on every `timeupdate`, and the composer resets its RHF
  // state whenever `defaultValues` changes, so the coach's
  // in-progress title / start / end / etc. would be wiped ~4×/s
  // during playback. `null` until clip-create mode is first
  // entered.
  const [clipCreateDefaults, setClipCreateDefaults] = useState<
    Partial<ClipComposerValues> | null
  >(null);
  // Focused feedback player modal — M5 coach-side scope. Opened from
  // the "Preview clip" / "Preview note" buttons in clip-edit /
  // note-edit mode (the bundle's seed clip / note is what's
  // previewed). Two independent flags — only one edit mode is
  // active at a time, but keeping them separate makes the render
  // condition for each modal explicit.
  const [clipPreviewOpen, setClipPreviewOpen] = useState(false);
  const [notePreviewOpen, setNotePreviewOpen] = useState(false);
  // Edit-mode clip defaults — hydrated from the bundle's seed clip.
  // Create-mode defaults are the frozen `clipCreateDefaults` state
  // above (NOT a per-render memo — see that state's comment).
  const clipEditDefaults = useMemo<
    Partial<ClipComposerValues> | undefined
  >(() => {
    const clip = bundle.data?.clip;
    if (!clip) return undefined;
    return {
      title: clip.title,
      description: clip.description ?? "",
      category: coerceNoteCategory(clip.category),
      visibility: clip.visibility,
      start_seconds: clip.start_seconds,
      end_seconds: clip.end_seconds,
      player_ids: clip.player_ids ?? [],
    };
  }, [bundle.data?.clip]);

  // Ref to the telestrator's backing `<video>` — forwarded to
  // whichever `<TelestratorCanvas>` is mounted (inline or focus
  // overlay). Drives the live time readout, the video keyboard
  // shortcuts, and — the M4 parity-audit P0-2 fix — the
  // `timestamp_seconds` a video note saves at.
  //
  // The element is tracked in STATE, not just a ref: the canvas
  // forwards it up through a callback ref deep inside
  // `<TelestratorCanvas>`, and nothing else would re-render
  // `CoachReview` when it mounts. `useVideoPlayhead` reads
  // `videoRef.current` at render time, so without a re-render it
  // would stay pinned to `null` and the readout would never leave
  // `--:--`. `setVideoEl` gives us that re-render; `videoElRef`
  // mirrors the state so the two hooks keep their `RefObject` API.
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const videoElRef = useRef<HTMLVideoElement | null>(null);
  videoElRef.current = videoEl;
  const handleVideoElRef = useCallback((el: HTMLVideoElement | null) => {
    setVideoEl(el);
  }, []);
  const playhead = useVideoPlayhead(videoElRef);
  useVideoKeyboardShortcuts({ videoRef: videoElRef, source });

  // ---- v2 clip track ----
  // The v2 layout puts a draggable IN/OUT clip track in the main
  // content column. `clipWindow` (absolute seconds) is the single
  // source of truth shared by the ClipTrack, the ClipPresetBar, the
  // ThumbnailStrip, and the ClipComposer's start/end defaults.
  //
  // It's derived from / re-seeded by the same `[t-5, t+8]` window the
  // ClipComposer uses (`clipCreateDefaults` / `clipEditDefaults`), but
  // held in its own state so a track drag updates it live without
  // churning the composer's other fields (the composer is only
  // re-seeded with the window at drawer-open time — see
  // `clipComposerKey`).
  const [clipWindow, setClipWindow] = useState<ClipWindow | null>(null);
  // Loop-within-window toggle. When on, the playhead bounces back to
  // `clipWindow.start` whenever it crosses `clipWindow.end`.
  const [loop, setLoop] = useState(false);
  // Media duration, tracked reactively off the backing `<video>` so
  // the clip track can map seconds → pixels. 0 until metadata loads.
  const [duration, setDuration] = useState(0);
  useEffect(() => {
    const el = videoEl;
    if (!el) {
      setDuration(0);
      return;
    }
    const sync = () => {
      setDuration(Number.isFinite(el.duration) ? el.duration : 0);
    };
    sync();
    el.addEventListener("loadedmetadata", sync);
    el.addEventListener("durationchange", sync);
    return () => {
      el.removeEventListener("loadedmetadata", sync);
      el.removeEventListener("durationchange", sync);
    };
  }, [videoEl]);

  // Deep-linking into a saved note should restore the same review
  // context as clicking the note on the timeline: load the match/slot,
  // seek the backing video to the saved timestamp, and show the saved
  // telestration over that frame. The video element can remount after
  // match/slot/source changes, so keep the pending seek separate from
  // the immediate click handler and apply it once the expected media is
  // mounted and metadata is available.
  const pendingVideoSeekRef = useRef<{
    matchId: string;
    slot: string;
    seconds: number;
  } | null>(null);

  const applyPendingVideoSeek = useCallback(() => {
    const pending = pendingVideoSeekRef.current;
    const video = videoElRef.current;
    if (!pending || !video) return false;
    if (pending.matchId !== matchId || pending.slot !== slot) return false;
    const hasMetadata =
      video.readyState >= HTMLMediaElement.HAVE_METADATA ||
      Number.isFinite(video.duration);
    if (!hasMetadata) return false;
    const max = Number.isFinite(video.duration) ? video.duration : pending.seconds;
    video.currentTime = clamp(pending.seconds, 0, max);
    video.pause();
    pendingVideoSeekRef.current = null;
    return true;
  }, [matchId, slot]);

  const queueVideoSeek = useCallback(
    (
      seconds: number,
      expectedMatchId: string | null | undefined,
      expectedSlot: string | null | undefined,
    ) => {
      if (!expectedMatchId || !expectedSlot) return;
      const normalizedSeconds = Math.max(0, seconds);
      pendingVideoSeekRef.current = {
        matchId: expectedMatchId,
        slot: expectedSlot,
        seconds: normalizedSeconds,
      };
      window.setTimeout(() => {
        void applyPendingVideoSeek();
      }, 0);
    },
    [applyPendingVideoSeek],
  );

  useEffect(() => {
    if (applyPendingVideoSeek()) return;
    const video = videoEl;
    if (!video) return;
    const onReady = () => {
      void applyPendingVideoSeek();
    };
    video.addEventListener("loadedmetadata", onReady);
    video.addEventListener("canplay", onReady);
    return () => {
      video.removeEventListener("loadedmetadata", onReady);
      video.removeEventListener("canplay", onReady);
    };
  }, [videoEl, applyPendingVideoSeek]);

  // Loop enforcement — bounce back to `clipWindow.start` when the
  // playhead crosses `clipWindow.end`. Distinct from
  // `useClipBoundaryWatcher` (which PAUSES at the boundary — used by
  // the feedback player); loop seeks back instead. A 250ms fallback
  // interval covers HLS `timeupdate` throttling, same as the
  // boundary watcher.
  useEffect(() => {
    const el = videoEl;
    if (!loop || !el || !clipWindow) return;
    const enforce = () => {
      if (el.currentTime >= clipWindow.end - 0.05) {
        el.currentTime = clipWindow.start;
        if (el.paused) void el.play().catch(() => undefined);
      }
    };
    el.addEventListener("timeupdate", enforce);
    const intervalId = window.setInterval(enforce, 250);
    return () => {
      el.removeEventListener("timeupdate", enforce);
      window.clearInterval(intervalId);
    };
  }, [videoEl, loop, clipWindow]);

  // Compute a FRESH clip-create window from the CURRENT playhead —
  // `[playhead − 5s, playhead + 8s]`, clamped to `[0, duration]`
  // and capped at the 120s max (ports the legacy "Save Clip"
  // pre-fill). Called only at the moments a re-seed is wanted
  // (entering clip-create mode, after a save) — NOT every render —
  // so a `timeupdate` tick can't churn the clip composer's
  // `defaultValues` and wipe in-progress edits.
  const freshClipWindow = useCallback((): Partial<ClipComposerValues> => {
    const t = playhead.currentTime ?? 0;
    const dur = videoElRef.current?.duration;
    const hardMax = Number.isFinite(dur) ? (dur as number) : Infinity;
    const start = Math.max(0, t - 5);
    const end = Math.min(
      hardMax,
      Math.min(start + MAX_CLIP_DURATION_SECONDS, t + 8),
    );
    return {
      start_seconds: Number(start.toFixed(1)),
      end_seconds: Number(end.toFixed(1)),
      visibility: "team",
      category: "other",
    };
  }, [playhead]);
  const resetClipWindowToPlayhead = useCallback(() => {
    const w = freshClipWindow();
    const start = w.start_seconds ?? 0;
    const end = w.end_seconds ?? start;
    setClipCreateDefaults(w);
    setClipWindow({ start, end });
  }, [freshClipWindow]);

  // Seed the frozen create-mode window ONCE when clip-create mode
  // is first entered (video surface, clip mode, not a clip-edit).
  // It's captured at entry time and then held — the coach's
  // subsequent edits + playback ticks don't disturb it. A save
  // re-seeds it explicitly (see `handleClipSubmit`). `clipCreate-
  // Defaults` is also reset to `null` when clip-create mode is
  // left, so the NEXT entry takes a fresh window from wherever the
  // playhead is then, rather than a stale one from a prior session.
  const inClipCreateMode =
    source === "video" && composerMode === "clip" && !isClipEdit;
  const wasClipCreateModeRef = useRef(false);
  useEffect(() => {
    if (inClipCreateMode) {
      setClipCreateDefaults((prev) => prev ?? freshClipWindow());
      // Seed the v2 clip track's window from the same `[t-5, t+8]`
      // window — once, on entry. A drag then owns it.
      setClipWindow((prev) => {
        if (prev) return prev;
        const w = freshClipWindow();
        return {
          start: w.start_seconds ?? 0,
          end: w.end_seconds ?? 0,
        };
      });
    } else if (wasClipCreateModeRef.current) {
      setClipCreateDefaults(null);
      // Leaving clip-create mode drops the window + loop so the next
      // entry starts clean.
      setClipWindow(null);
      setLoop(false);
    }
    wasClipCreateModeRef.current = inClipCreateMode;
    // `freshClipWindow` changes identity with the playhead — but
    // it's only CALLED inside the `prev ?? …` guard, so once the
    // window is frozen a playhead tick re-running this effect is a
    // no-op (`prev` is non-null). Intentional: the effect re-runs,
    // the state doesn't change, no churn.
  }, [inClipCreateMode, freshClipWindow]);

  // Clip-EDIT mode — seed the track window from the bundle's seed
  // clip (its `start_seconds` / `end_seconds`). Distinct path from
  // create mode: edit windows come from the persisted clip, not the
  // playhead.
  useEffect(() => {
    const clip = bundle.data?.clip;
    if (!isClipEdit || !clip) return;
    setClipWindow({
      start: clip.start_seconds,
      end: clip.end_seconds,
    });
  }, [isClipEdit, bundle.data?.clip]);

  // ---- Timeline chip rail (M4 audit → M5) ----
  // The rail under the video shows the selected match's video
  // notes. `useCoachNotes` is gated on `matchId` — no match, no
  // fetch (the rail renders its own no-match state). `activeNoteId`
  // tracks the last-seeked chip; it also feeds the Save Clip
  // `source_note_id` derivation in a later slice.
  const coachNotesQuery = useCoachNotes(matchId || undefined);
  const railNotes = coachNotesQuery.data?.notes ?? [];
  // The selected match's clips — feeds the clips rail + the
  // clip-track existing-clips lane. Same scope/match gating as
  // `useCoachNotes`; an `invalidateScope` from a clip save refetches.
  const coachClipsQuery = useCoachClips(matchId || undefined);
  // Tactical-board mode rail data rides the mount-time bundle (see the
  // single mount-time fetch rule in AGENTS.md) — no parallel fetch.
  const recentObservations = bundle.data?.recent_observations ?? [];
  const [activeNoteId, setActiveNoteId] = useState<number | null>(null);
  const [activeNoteMoment, setActiveNoteMoment] = useState<number | null>(null);

  // Click a chip → jump the video to that note's moment and
  // restore its drawing snapshot onto the canvas. Ports legacy
  // `seekCoachReviewNote`. When the note belongs to a different
  // match/slot than what's loaded, switch the picker first — the
  // `<HlsVideo>` remounts on the match change and the seek lands
  // once the new source loads (the `seeked`/`loadedmetadata`
  // subscription in `useVideoPlayhead` keeps the readout honest).
  const handleSeekNote = useCallback(
    (note: CoachingNote) => {
      setActiveNoteId(note.id);
      const target = Math.max(0, Number(note.timestamp_seconds ?? 0));
      setActiveNoteMoment(target);
      const targetMatchId = note.match_id ?? matchId;
      const targetSlot = note.slot ?? slot;
      if (note.match_id && note.match_id !== matchId) setMatchId(note.match_id);
      if (note.slot && note.slot !== slot) setSlot(note.slot);
      // Restore the note's drawing snapshot (or clear to empty).
      // `note.drawing` is the on-disk shape — `loadDrawing` converts
      // it to the in-memory shape the canvas expects.
      const nextDrawing =
        note.drawing && Object.keys(note.drawing).length > 0
          ? loadDrawing(note.drawing)
          : emptyDrawing();
      setDrawing(nextDrawing);
      queueVideoSeek(target, targetMatchId, targetSlot);
    },
    [matchId, queueVideoSeek, slot],
  );

  const openClipForEdit = useCallback(
    (clip: CoachingClip) => {
      const clipStart = Math.max(0, Number(clip.start_seconds ?? 0));
      setSource("video");
      setComposerMode("clip");
      setComposing(false);
      if (clip.match_id && clip.match_id !== matchId) setMatchId(clip.match_id);
      if (clip.slot && clip.slot !== slot) setSlot(clip.slot);
      setClipWindow({
        start: clip.start_seconds,
        end: clip.end_seconds,
      });
      setDrawing(
        clip.drawing && Object.keys(clip.drawing).length > 0
          ? loadDrawing(clip.drawing)
          : emptyDrawing(),
      );
      setActiveNoteId(null);
      setActiveNoteMoment(clipStart);
      queueVideoSeek(clipStart, clip.match_id, clip.slot);
      void navigate({
        to: "/coach/review",
        search: {
          match_id: clip.match_id,
          clip_id: clip.id,
          source: "video",
        },
      });
    },
    [matchId, navigate, queueVideoSeek, slot],
  );

  const autoOpenedNoteKeyRef = useRef<string | null>(null);
  const autoOpenedClipKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const clip = bundle.data?.clip;
    if (!clip) return;
    const key = [
      clip.id,
      clip.match_id,
      clip.slot,
      clip.start_seconds,
      clip.end_seconds,
    ].join(":");
    if (autoOpenedClipKeyRef.current === key) return;
    autoOpenedClipKeyRef.current = key;
    setSource("video");
    setComposerMode("clip");
    setActiveNoteId(null);
    setActiveNoteMoment(Math.max(0, Number(clip.start_seconds ?? 0)));
    queueVideoSeek(clip.start_seconds, clip.match_id, clip.slot);
  }, [bundle.data?.clip, queueVideoSeek]);

  useEffect(() => {
    const note = bundle.data?.note;
    if (!note || note.note_context !== "video") return;
    const key = [
      note.id,
      note.match_id ?? "",
      note.slot ?? "",
      note.timestamp_seconds ?? 0,
    ].join(":");
    if (autoOpenedNoteKeyRef.current === key) return;
    autoOpenedNoteKeyRef.current = key;
    setSource("video");
    setComposerMode("note");
    handleSeekNote(note);
  }, [bundle.data?.note, handleSeekNote]);

  // ---- v2 clip-track handlers ----
  // Seek the backing `<video>` to an absolute time. The clip track,
  // preset bar, thumbnail strip, and frame stepper all route through
  // this so there's one seek path; `useVideoPlayhead` re-renders the
  // readouts off the resulting `seeked` event.
  const seekVideo = useCallback((seconds: number) => {
    const v = videoElRef.current;
    if (!v) return;
    const dur = Number.isFinite(v.duration) ? v.duration : Infinity;
    v.currentTime = clamp(seconds, 0, dur);
  }, []);

  const DRAWING_FRAME_TOLERANCE_SECONDS = 0.5;
  // The moment the canvas drawing is anchored to:
  //   - clip mode → the clip window's FIRST frame. A clip's drawing
  //     is a freeze-frame over its first frame — the same contract
  //     the feedback player replays (it shows the clip drawing only
  //     while paused at `start_seconds` and hides it on Play). The
  //     anchor follows the live window, so dragging the IN handle
  //     re-anchors instead of staying pinned to a stale moment.
  //   - note mode → the active saved note's moment (null while the
  //     coach is free-drawing a new note at the paused frame).
  const videoDrawingAnchor =
    source === "video" && composerMode === "clip"
      ? (clipWindow?.start ?? null)
      : activeNoteMoment;
  const showVideoDrawing =
    source !== "video" ||
    videoDrawingAnchor == null ||
    (playhead.paused &&
      (playhead.currentTime == null ||
        Math.abs(playhead.currentTime - videoDrawingAnchor) <=
          DRAWING_FRAME_TOLERANCE_SECONDS));

  // Release a loaded note's pin once the coach parks somewhere else.
  // Clicking a note mark/chip loads its drawing pinned to its moment
  // and the canvas hides that drawing away from the pinned frame.
  // Without a release the pin outlives the visit: the coach scrubs
  // to a new moment and every new stroke joins the HIDDEN saved
  // drawing — "can't telestrate again". When the playhead settles
  // (paused) outside the tolerance, drop the pin and start a fresh
  // canvas. Skipped:
  //   - in note-edit mode (the pin IS the note being edited — its
  //     drawing must survive a scrub-away + return);
  //   - in clip mode (the anchor derives from the clip window);
  //   - while a queued seek is in flight (chip clicks across
  //     match/slot land late — the stale playhead isn't a "park").
  // Reads the LIVE element time rather than playhead state: a just-
  // applied pending seek updates `video.currentTime` synchronously
  // but the `seeked` event (which refreshes playhead state) lands a
  // tick later — state alone could release a pin that was satisfied.
  useEffect(() => {
    if (source !== "video" || composerMode === "clip" || isNoteEdit) return;
    if (activeNoteMoment == null) return;
    if (pendingVideoSeekRef.current) return;
    const v = videoElRef.current;
    if (!v || !v.paused) return;
    const t = v.currentTime;
    if (!Number.isFinite(t)) return;
    if (Math.abs(t - activeNoteMoment) <= DRAWING_FRAME_TOLERANCE_SECONDS) {
      return;
    }
    setActiveNoteId(null);
    setActiveNoteMoment(null);
    setDrawing(emptyDrawing());
    // playhead.paused/currentTime are the re-run triggers (they tick
    // on seeked/pause); the live element is the source of truth.
  }, [
    source,
    composerMode,
    isNoteEdit,
    activeNoteMoment,
    playhead.paused,
    playhead.currentTime,
  ]);

  // Apply a clip window from a drag / preset, clamped to the media.
  const applyClipWindow = useCallback(
    (next: ClipWindow) => {
      const dur = duration > 0 ? duration : Infinity;
      setClipWindow({
        start: clamp(next.start, 0, dur),
        end: clamp(next.end, 0, dur),
      });
    },
    [duration],
  );

  // Set the clip IN point at the current playhead — keeps OUT at
  // least 5 frames ahead. Shared by the `I` shortcut + the preset bar.
  const setClipIn = useCallback(() => {
    const t = playhead.currentTime ?? 0;
    setClipWindow((prev) => {
      const end = prev ? Math.max(prev.end, t + FRAME * 5) : t + 0.5;
      return { start: t, end };
    });
  }, [playhead]);

  // Set the clip OUT point at the current playhead — keeps IN at
  // least 5 frames behind. When that minimum-separation clamp has to
  // MOVE the IN point, flag the park-suppression ref: setting OUT
  // must leave the playhead on the OUT frame (the coach is looking
  // at the frame they're cutting at), but the clamp's incidental
  // `start` change would otherwise fire the first-frame parking
  // effect below and yank the playhead away.
  const suppressClipParkRef = useRef(false);
  const setClipOut = useCallback(() => {
    const t = playhead.currentTime ?? 0;
    const prev = clipWindow;
    const start = Math.max(
      0,
      prev ? Math.min(prev.start, t - FRAME * 5) : Math.max(0, t - 0.5),
    );
    if (!prev || start !== prev.start) suppressClipParkRef.current = true;
    setClipWindow({ start, end: t });
  }, [playhead, clipWindow]);

  // Park the playhead on the clip's FIRST frame whenever the IN
  // boundary moves (preset, `I` key, handle drag, edit load). A
  // clip's telestration is a freeze-frame over its first frame —
  // parking there makes the canvas immediately visible/editable
  // (see `videoDrawingAnchor`) and previews exactly the frame the
  // feedback player will hold before Play. Keyed on the start VALUE
  // so loop bounces and OUT-handle drags don't re-seek; `setClipOut`
  // suppresses the park when its separation clamp moves `start` as a
  // side effect (the flag is only raised when `start` changes, so a
  // consumed-or-never-raised flag can't leak into later parks).
  const clipWindowStart = clipWindow?.start;
  useEffect(() => {
    if (source !== "video" || composerMode !== "clip") return;
    if (clipWindowStart == null) return;
    if (suppressClipParkRef.current) {
      suppressClipParkRef.current = false;
      return;
    }
    seekVideo(clipWindowStart);
  }, [source, composerMode, clipWindowStart, seekVideo]);

  // Explicit "back to the first frame". Setting OUT keeps the
  // playhead on the out frame (the coach needs to see what they're
  // cutting at), so this is the deliberate way back to the freeze
  // frame the clip's telestration is anchored to. The preset-bar
  // button and the `[` shortcut share this one path.
  const goToClipFirstFrame = useCallback(() => {
    if (clipWindow) seekVideo(clipWindow.start);
  }, [clipWindow, seekVideo]);

  const handleTemplateApply = (
    values: Partial<ComposerValues> & { __appliedTemplateId: string },
  ) => {
    const { __appliedTemplateId: _unused, ...fields } = values;
    void _unused;
    // Build the override on top of the LIVE form state so the
    // coach's unsaved edits in fields the template doesn't write
    // (visibility, tags, player_ids, coach_private_note) survive
    // the template apply. Templates only overwrite their declared
    // fields; everything else stays.
    const live = composerRef.current?.getValues();
    setComposerOverride({ ...(live ?? composerDefaults ?? {}), ...fields });
    setComposerKey((k) => k + 1);
  };

  // ---- Help popover state ----
  // (focusOpen + focusButtonRef declared earlier so width budget can
  // read focusOpen.)
  const [helpOpen, setHelpOpen] = useState(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);

  // M7.5 B7: Escape closes the popover (standard dialog behavior).
  useEffect(() => {
    if (!helpOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setHelpOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [helpOpen]);

  // Snapshot whichever composer is currently live into its
  // override, so it survives the focus-mode portal remount. The
  // rail hosts EITHER the note/observation composer (`composerRef`)
  // OR the clip composer (`clipComposerRef`) depending on
  // `composerMode` — in clip mode `composerRef.current` is null and
  // the live form is behind `clipComposerRef`, so a note-only
  // snapshot would silently drop the coach's clip edits across the
  // transition. Snapshot the one that's mounted.
  const snapshotActiveComposer = () => {
    if (composerMode === "clip") {
      const liveClip = clipComposerRef.current?.getValues();
      if (liveClip) setClipComposerOverride(liveClip);
    } else {
      const liveNote = composerRef.current?.getValues();
      if (liveNote) setComposerOverride(liveNote);
    }
  };

  // Open focus mode: snapshot live form values into the override so
  // when the inline composer remounts on close, it picks up any
  // edits the coach made BEFORE clicking focus. The composer node
  // is re-parented into the focus-mode floating panel while focus
  // is open (it's NOT unmounted), but the snapshot is the
  // symmetric bookend to `closeFocus` and guards the inline form's
  // continuity.
  const openFocus = () => {
    snapshotActiveComposer();
    // The drawer and focus mode both host the composer; close the
    // drawer so the form mounts only inside the focus panel.
    setComposing(false);
    setMobileToolsOpen(false);
    setFocusOpen(true);
  };
  const closeFocus = () => {
    // Snapshot the live composer values BEFORE flipping focusOpen.
    // The composer node lives inside the focus-mode floating panel
    // while focus mode is open; closing unmounts it and remounts a
    // fresh instance inline. Without this snapshot any edits the
    // coach made inside focus mode (in EITHER composer) are lost on
    // exit.
    snapshotActiveComposer();
    setFocusOpen(false);
    // Return focus to the trigger on close. We do it after the
    // state flip so the inline composer has remounted (it's
    // unmounted while focusOpen) and the focus call lands inside
    // a regular non-inert subtree.
    queueMicrotask(() => focusButtonRef.current?.focus());
  };

  // ---- Composer drawer open / close (v2) ----
  // Opening for a note (`M` / "Mark moment") still opens the drawer
  // immediately. Clip authoring is a two-step flow: first
  // enter clip mode so the IN/OUT controls remain visible, then open
  // the drawer when the coach is ready to save details.
  const openComposerNote = () => {
    if (!isClipEdit) setComposerMode("note");
    setComposing(true);
  };
  const enterClipMode = () => {
    // Clip mode is video-only; the tactical board has no clips.
    if (source !== "video") {
      openComposerNote();
      return;
    }
    if (isNoteEdit) return;
    setComposerMode("clip");
    if (!isClipEdit) setComposing(false);
  };
  const openComposerClip = () => {
    // Clip mode is video-only; the tactical board has no clips.
    if (source === "video" && !isNoteEdit) setComposerMode("clip");
    setComposing(true);
  };
  const exitClipMode = () => {
    if (isNoteEdit || isClipEdit) return;
    setComposerMode("note");
    setComposing(false);
    setClipWindow(null);
    setClipCreateDefaults(null);
    setClipComposerOverride(undefined);
    setLoop(false);
  };
  const startNewClipFromEdit = () => {
    const targetMatchId =
      matchId || bundle.data?.clip?.match_id || search.match_id || "";
    setSource("video");
    setComposerMode("clip");
    setComposing(false);
    setClipComposerOverride(undefined);
    setClipComposerKey((k) => k + 1);
    setClipCreateDefaults(null);
    setClipWindow(null);
    setDrawing(emptyDrawing());
    setActiveNoteId(null);
    setActiveNoteMoment(null);
    setLoop(false);
    void navigate({
      to: "/coach/review",
      search: targetMatchId
        ? { match_id: targetMatchId, source: "video" }
        : { source: "video" },
    });
  };
  const startNewNoteFromEdit = () => {
    const targetMatchId =
      matchId || bundle.data?.note?.match_id || search.match_id || "";
    setSource("video");
    setComposerMode("note");
    setComposerOverride({});
    setComposerKey((k) => k + 1);
    setComposing(true);
    setDrawing(emptyDrawing());
    setActiveNoteId(null);
    setActiveNoteMoment(null);
    setLoop(false);
    void navigate({
      to: "/coach/review",
      search: targetMatchId
        ? { match_id: targetMatchId, source: "video" }
        : { source: "video" },
    });
  };
  const startNewObservationFromEdit = () => {
    setSource("tactical_board");
    setComposerMode("note");
    setComposerOverride({});
    setComposerKey((k) => k + 1);
    setComposing(true);
    setScene(emptyScene());
    setActiveNoteId(null);
    setActiveNoteMoment(null);
    setLoop(false);
    void navigate({
      to: "/coach/review",
      search: { source: "tactical_board" },
    });
  };
  const closeComposer = () => {
    // Snapshot the live form in EVERY mode, not just edit. The
    // drawer unmounts its children on close, and the tactical-board
    // workflow is open-drawer → type → close → draw on the board →
    // reopen; without the snapshot, create mode silently dropped
    // everything typed on that round trip. The post-save reset
    // clears the snapshot and resets the live form, so a SAVED note
    // reopens at defaults — except the M7.5 B8 sticky fields
    // (note_type / category / visibility / player_ids), which
    // intentionally carry over so a coach can save several notes on
    // one player without re-tagging.
    snapshotActiveComposer();
    setComposing(false);
    if (isNoteEdit || isClipEdit) return;
    // Return focus to whichever trigger is in the stage control bar.
    queueMicrotask(() => composeButtonRef.current?.focus());
  };

  useEffect(() => {
    if (!mobileToolsOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileToolsOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileToolsOpen]);

  // ---- Source switching (shared path) ----
  // The page tabs, the `V`/`B` shortcuts, and the drawer's compact
  // toggle (#366) all flip the source through here: snapshot the
  // live composer first so the canvas remount keeps unsaved edits.
  const switchSource = useCallback(
    (next: CoachReviewSource) => {
      if (next === source) return;
      const live = composerRef.current?.getValues();
      if (live) setComposerOverride(live);
      setSource(next);
    },
    [source],
  );

  // ---- Keyboard shortcuts ----
  // The tool letters + `?` + `Esc` are handled inside the hook;
  // here we wire the v2 review-action keys to Coach Review state.
  // `C` enters clip range mode, `M` opens the mark-moment drawer, and
  // `I` / `O` set the clip window. On the tactical board `C` falls
  // back to a note open (no clips there). Frame step routes through the same
  // `seekVideo` the FrameStepper buttons use.
  useReviewKeyboardShortcuts({
    onToggleHelp: () => setHelpOpen((v) => !v),
    onEnterClip: () => enterClipMode(),
    onMarkMoment: () => openComposerNote(),
    onSetClipIn: () => {
      if (source === "video" && composerMode === "clip") setClipIn();
    },
    onSetClipOut: () => {
      if (source === "video" && composerMode === "clip") setClipOut();
    },
    onGoToClipIn: () => {
      if (source === "video" && composerMode === "clip") goToClipFirstFrame();
    },
    onFrameStep: (direction, shift) => {
      const v = videoElRef.current;
      if (!v) return;
      const delta = (shift ? 1 : FRAME) * direction;
      seekVideo(v.currentTime + delta);
    },
    // `L` — toggle loop. Only meaningful with a clip window; a no-op
    // otherwise (the loop effect is gated on `clipWindow`).
    onToggleLoop: () => setLoop((v) => !v),
    // `F` — toggle focus mode (the same open/close the Focus button
    // drives).
    onToggleFocus: () => {
      if (focusOpen) closeFocus();
      else openFocus();
    },
    // `V` / `B` — switch the active source surface.
    onSwitchSource: switchSource,
  });

  // ---- Save handler ----
  // Blank-wipe guard: the PATCH always carries the CURRENT board
  // scene, so updating an observation whose saved board had content
  // while the live board is empty (Clear Board, a not-yet-seeded
  // scene) silently destroys the drawing. Park the submitted values
  // and confirm before letting that save through.
  const [pendingWipeValues, setPendingWipeValues] =
    useState<ComposerValues | null>(null);
  const handleSubmit: SubmitHandler<ComposerValues> = async (values) => {
    if (search.note_id && source === "tactical_board") {
      const liveBoardEmpty =
        scene.tokens.length === 0 && scene.shapes.length === 0;
      const original = bundle.data?.note?.tactical_board_json;
      const parsedOriginal = original ? sceneFromOnDisk(original) : null;
      const savedBoardHasContent = parsedOriginal?.ok
        ? parsedOriginal.value.tokens.length +
            parsedOriginal.value.shapes.length >
          0
        : false;
      if (liveBoardEmpty && savedBoardHasContent) {
        setPendingWipeValues(values);
        return;
      }
    }
    await submitNoteValues(values);
  };

  const submitNoteValues = async (values: ComposerValues) => {
    // The note CONTEXT is driven by the active SOURCE, not by
    // whether a match happens to be in scope:
    //   - video surface          → a video coaching note
    //   - tactical_board surface → an observation note
    // (This was previously keyed off `bundle.data?.match`, which
    // meant video mode with no preloaded match silently saved an
    // observation — wrong.)
    const isObservation = source === "tactical_board";

    // Merge canvas data based on the active source.
    // tactical_board_json + drawing are SEPARATE columns on
    // coaching_notes, so a single POST/PATCH can carry both. We
    // only emit the field for the active source — the inactive
    // surface's content stays in memory but doesn't ride along
    // (an unused empty scene/drawing in the payload is harmless,
    // but skipping keeps wire traffic + the audit log small).
    // Both canvas blobs are the canonical IN-MEMORY shapes; the
    // backend stores the ON-DISK shapes. The serializers do the
    // boundary rename:
    //   - `saveDrawing`: telestrator `kind`→`type`,
    //     `strokeWidth`→`width`. Skipping it sends `kind`-keyed
    //     objects, which the backend HARD-rejects with a 422
    //     ("drawing object type must be one of …").
    //   - `sceneToOnDisk`: tactical-board `strokeWidth`→
    //     `stroke_width`. Skipping it doesn't 422 (the board
    //     validator is allowlist-style) — it SILENTLY drops the
    //     stroke width, so a thick/thin board shape saves at the
    //     default-3 fallback.
    const canvasFields =
      source === "tactical_board"
        ? { tactical_board_json: sceneToOnDisk(scene) }
        : { drawing: saveDrawing(drawing) as unknown as Record<string, unknown> };

    // Video notes need a match anchor. Block the save with a
    // visible error rather than letting the backend 422.
    if (!isObservation && !matchId) {
      setMatchError("Pick a match before saving a video note.");
      return;
    }
    setMatchError(null);

    if (search.note_id) {
      // PATCH path. `UpdateCoachingNoteRequest` accepts the
      // source/anchor/event fields too — the backend applies only
      // what we send (`model_dump(exclude_unset=True)`) and
      // revalidates the MERGED row. So the PATCH must carry the
      // active source's context the same way POST does, or:
      //   - editing an observation's event_title/date/type silently
      //     wouldn't persist, and
      //   - switching video ↔ tactical board on an existing note
      //     would leave the stale `note_context` + anchor.
      //
      // `body` carries the composer's long-form notes field — it
      // used to be hardcoded `""` here, which silently wiped the
      // stored `body` on every edit (M4 parity-audit P0-1).
      const originalContext = bundle.data?.note?.note_context;
      const isContextFlip =
        (isObservation ? "observation" : "video") !== originalContext;

      const contextFields: Partial<UpdateCoachingNoteRequest> = isObservation
        ? {
            note_context: "observation",
            // Observation event metadata — persist the composer's
            // event fields.
            event_title: values.event_title,
            event_date: values.event_date,
            event_type: values.event_type,
            // On a video→observation FLIP, clear the match anchor.
            // The stored match_id/slot/timestamp are NOT inert:
            // `db.list_coaching_notes(match_id=...)` filters purely
            // on match_id (no note_context guard), so a converted
            // observation that kept its match_id would still show
            // up in that match's note lists/timelines. POSTed
            // observations use null anchors — a flipped one must
            // match. (A note that was always an observation never
            // had anchors, so we only send the nulls on a flip.)
            ...(isContextFlip
              ? { match_id: null, slot: null, timestamp_seconds: null }
              : {}),
          }
        : {
            note_context: "video",
            // A video note needs the match anchor. Always send
            // match_id + slot so an observation→video flip lands a
            // coherent row (the merged-state validator requires
            // all three). `timestamp_seconds` is only sent on a
            // context FLIP — for an in-place video edit we must
            // NOT clobber the note's stored timestamp with 0.
            match_id: matchId,
            slot,
            ...(isContextFlip ? { timestamp_seconds: 0 } : {}),
          };

      const body: UpdateCoachingNoteRequest = {
        title: values.title,
        body: values.body,
        category: values.category,
        visibility: values.visibility,
        note_type: values.note_type,
        player_summary: values.player_summary,
        what_happened: values.what_happened,
        why_it_matters: values.why_it_matters,
        what_to_do_next: values.what_to_do_next,
        coach_private_note: values.coach_private_note,
        player_ids: values.player_ids,
        tags: parseTagsRaw(values.tags_raw),
        ...contextFields,
        ...canvasFields,
      };
      try {
        await saveNote.mutateAsync({
          mode: "update",
          id: search.note_id,
          body,
        });
      } catch (err) {
        // Keep the form intact so the coach can retry — skip the
        // post-save reset below. The inline error block stays as
        // secondary detail; the toast is the at-a-glance signal.
        toast.error(
          isObservation ? "Couldn't save observation" : "Couldn't save note",
          { description: errorMessage(err) },
        );
        return;
      }
      toast.success(isObservation ? "Observation updated" : "Note updated");
      return;
    } else {
      // POST path. Video notes carry the match/slot anchor the
      // coach picked + the `timestamp_seconds` the player is at.
      //
      // M4 parity-audit P0-2 FIX: this used to be hardcoded `0` —
      // M4 had no player to scrub against — so every M4 video note
      // was silently pinned to 0:00. M5's `<HlsVideo>` exposes the
      // playhead via `useVideoPlayhead`; the note now saves at the
      // coach's actual position. `playhead.currentTime` is `null`
      // until the video loads — if the coach saves that early we
      // fall back to `0` (a valid `ge=0` timestamp; the note lands
      // at the slot start, same as a deliberate scrub-to-start).
      //
      // Observation notes pass null match/slot/timestamp + the
      // event fields (generated-required-with-default because
      // Pydantic gives them `Field(default="...")`).
      const body: CreateCoachingNoteRequest = {
        match_id: isObservation ? null : matchId,
        slot: isObservation ? null : slot,
        timestamp_seconds: isObservation ? null : (playhead.currentTime ?? 0),
        title: values.title,
        body: values.body,
        category: values.category,
        visibility: values.visibility,
        note_type: values.note_type,
        note_context: isObservation ? "observation" : "video",
        // Observation event metadata comes from the composer's
        // event fields (rendered only in tactical-board mode). For
        // a video note these stay empty — the backend ignores
        // event_* when `note_context === "video"`.
        event_title: isObservation ? values.event_title : "",
        event_date: isObservation ? values.event_date : "",
        event_type: isObservation ? values.event_type : "",
        player_summary: values.player_summary,
        what_happened: values.what_happened,
        why_it_matters: values.why_it_matters,
        what_to_do_next: values.what_to_do_next,
        coach_private_note: values.coach_private_note,
        player_ids: values.player_ids,
        tags: parseTagsRaw(values.tags_raw),
        ...canvasFields,
      };
      try {
        await saveNote.mutateAsync({ mode: "create", body });
      } catch (err) {
        toast.error(
          isObservation ? "Couldn't save observation" : "Couldn't save note",
          { description: errorMessage(err) },
        );
        return;
      }
      toast.success(isObservation ? "Observation saved" : "Note saved");
    }

    // M7.5 B8: per-moment sticky fields. Legacy
    // (`js/coaching/review.js:2566-2595`) clears only the
    // per-moment subset on save and keeps `category` /
    // `visibility` / `note_type` / `player_ids` sticky so a
    // coach reviewing one player can save several notes in a row
    // without re-tagging. Mirror that here: read the live form,
    // reset to DEFAULTS but with the sticky fields preserved.
    setComposerOverride(undefined);
    const live = composerRef.current?.getValues();
    if (live) {
      composerRef.current?.reset({
        // Sticky: keep these across the save.
        note_type: live.note_type,
        category: live.category,
        visibility: live.visibility,
        player_ids: live.player_ids,
        // Per-moment: everything else falls back to DEFAULTS via the
        // composer's `reset({ ...DEFAULTS, ...overrides })` merge.
      });
    } else {
      // No live values (composer not mounted) — fall back to a
      // full remount.
      setComposerKey((k) => k + 1);
    }

    // Reset the canvas state on the active source. Without this,
    // the next note saved from the same source silently inherits
    // the just-saved tactical board / drawing — stale evidence
    // attached to an unrelated note. We only clear the active
    // source so a coach mid-flow on the OTHER surface keeps their
    // in-progress work (a tactical sketch should survive a quick
    // video-note save, and vice versa).
    if (source === "tactical_board") {
      setScene(emptyScene());
    } else {
      setDrawing(emptyDrawing());
      setActiveNoteId(null);
      setActiveNoteMoment(null);
    }
  };

  // ---- Clip: source-note derivation ----
  // A clip's `source_note_id` is the note whose drawing snapshot the
  // clip should inherit. Ports legacy `_deriveClipSourceNoteId`:
  //   1. prefer the timeline chip the coach last clicked
  //      (`activeNoteId`) — but only if it's a VIDEO note on the
  //      same match/slot as the clip window;
  //   2. else fall back to a co-located note — a video note on the
  //      same match/slot whose `timestamp_seconds` falls inside the
  //      `[start, end]` window, the one closest to the window
  //      midpoint when several match.
  // CREATE-only — never re-derived on edit (a clip's source is
  // immutable; `UpdateCoachingClipRequest` doesn't carry the field).
  const deriveClipSourceNoteId = useCallback(
    (start: number, end: number): number | null => {
      const candidates = railNotes.filter(
        (n) =>
          n.note_context === "video" &&
          n.match_id === matchId &&
          n.slot === slot,
      );
      if (candidates.length === 0) return null;
      // (1) the active chip, if it qualifies + is in-window.
      if (activeNoteId != null) {
        const active = candidates.find((n) => n.id === activeNoteId);
        if (
          active &&
          Number(active.timestamp_seconds ?? -1) >= start &&
          Number(active.timestamp_seconds ?? -1) <= end
        ) {
          return active.id;
        }
      }
      // (2) co-located note closest to the window midpoint.
      const mid = (start + end) / 2;
      const inWindow = candidates
        .filter((n) => {
          const t = Number(n.timestamp_seconds ?? -1);
          return t >= start && t <= end;
        })
        .sort(
          (a, b) =>
            Math.abs(Number(a.timestamp_seconds ?? 0) - mid) -
            Math.abs(Number(b.timestamp_seconds ?? 0) - mid),
        );
      return inWindow[0]?.id ?? null;
    },
    [railNotes, matchId, slot, activeNoteId],
  );

  // ---- Clip save handler ----
  const handleClipSubmit: SubmitHandler<ClipComposerValues> = async (
    values,
  ) => {
    // A clip always needs a match anchor. Block the save with the
    // same inline error the note path uses rather than 422-ing.
    if (!matchId) {
      setMatchError("Pick a match before saving a clip.");
      return;
    }
    setMatchError(null);

    if (search.clip_id) {
      // PATCH path. `UpdateCoachingClipRequest` is `extra="forbid"`
      // and OMITS match_id/slot — a clip's anchor is immutable.
      // `source_note_id` is CREATE-only and not in the Update type.
      const body: UpdateCoachingClipRequest = {
        title: values.title,
        description: values.description,
        category: values.category,
        visibility: values.visibility,
        start_seconds: values.start_seconds,
        end_seconds: values.end_seconds,
        player_ids: values.player_ids,
        drawing: saveDrawing(drawing) as unknown as Record<string, unknown>,
      };
      try {
        await saveClip.mutateAsync({
          mode: "update",
          id: search.clip_id,
          body,
        });
      } catch (err) {
        toast.error("Couldn't save clip", { description: errorMessage(err) });
        return;
      }
      toast.success("Clip updated");
    } else {
      // POST path. The clip carries the match/slot anchor + the
      // window + the derived source note + the canvas drawing
      // snapshot. `source_note_id` is sent only on create.
      const sourceNoteId = deriveClipSourceNoteId(
        values.start_seconds,
        values.end_seconds,
      );
      const body: CreateCoachingClipRequest = {
        match_id: matchId,
        slot,
        start_seconds: values.start_seconds,
        end_seconds: values.end_seconds,
        title: values.title,
        description: values.description,
        category: values.category,
        visibility: values.visibility,
        player_ids: values.player_ids,
        ...(sourceNoteId != null ? { source_note_id: sourceNoteId } : {}),
        // Serialize to the backend's on-disk shape (`type`-keyed
        // objects) — see the note-save comment above.
        drawing: saveDrawing(drawing) as unknown as Record<string, unknown>,
      };
      try {
        await saveClip.mutateAsync({ mode: "create", body });
      } catch (err) {
        toast.error("Couldn't save clip", { description: errorMessage(err) });
        return;
      }
      toast.success("Clip saved");
    }

    if (search.clip_id) return;

    // Reset create-mode clip authoring so the coach can immediately
    // mark the next clip from the current playhead.
    //   - drop the focus-mode snapshot override so it doesn't
    //     re-seed the next clip with the just-saved values,
    //   - key-bump to remount the composer at fresh defaults,
    //   - clear the canvas drawing,
    //   - reseed the IN/OUT window around the current time,
    //   - close the drawer but stay in clip mode so the range tools
    //     remain available.
    setClipComposerOverride(undefined);
    setClipComposerKey((k) => k + 1);
    setDrawing(emptyDrawing());
    setActiveNoteId(null);
    setActiveNoteMoment(null);
    resetClipWindowToPlayhead();
    setLoop(false);
    setComposing(false);
  };

  // ---- Render ----
  const players = bundle.data?.players ?? [];
  // Composer + template selector live INSIDE the canvas's right
  // rail via `toolbarChildren`. The canvas's built-in tool grid /
  // color / stroke / formation block sit above; the composer
  // continues the rail downward. Matches the legacy Replay coach-
  // review layout (single right column = tools + composer; left
  // column = video / pitch).
  // Submit label keys off the active SOURCE (video → note,
  // tactical_board → observation), not off whether a match is
  // loaded. Edit mode shows "Update …" for whichever kind. Note-
  // specific — the clip composer owns its own button label.
  const submitLabel = saveNote.isError
    ? "Retry save"
    : source === "tactical_board"
      ? isNoteEdit
        ? "Update observation"
        : "Save observation"
      : isNoteEdit
        ? "Update note"
        : "Save note";

  const editDetailsLabel = isClipEdit
    ? "Edit clip details"
    : source === "tactical_board"
      ? "Edit observation details"
      : "Edit note details";
  const startFreshEditAction =
    isClipEdit
      ? {
          label: "New clip",
          icon: <PlusIcon size={14} strokeWidth={2.5} />,
          onClick: startNewClipFromEdit,
          testId: "coach-review-new-clip-from-edit",
        }
      : source === "tactical_board"
        ? {
            label: "New observation",
            icon: <PlusIcon size={14} strokeWidth={2.5} />,
            onClick: startNewObservationFromEdit,
            testId: "coach-review-new-observation-from-edit",
          }
        : {
            label: "New note",
            icon: <PlusIcon size={14} strokeWidth={2.5} />,
            onClick: startNewNoteFromEdit,
            testId: "coach-review-new-note-from-edit",
          };
  const renderEditActions = () =>
    (isNoteEdit || isClipEdit) && !composing ? (
      <>
        <button
          ref={composeButtonRef}
          type="button"
          onClick={isClipEdit ? openComposerClip : openComposerNote}
          data-testid="coach-review-edit-details"
          className="inline-flex min-h-10 flex-1 items-center justify-center gap-1.5 rounded bg-accent px-3 py-1.5 font-display text-[13px] font-semibold text-accent-ink transition-colors hover:bg-accent-hover sm:min-h-0 sm:flex-none sm:justify-start"
        >
          {isClipEdit ? <FilmIcon size={14} /> : <PencilIcon size={14} />}
          {editDetailsLabel}
        </button>
        <button
          type="button"
          onClick={startFreshEditAction.onClick}
          data-testid={startFreshEditAction.testId}
          className="inline-flex min-h-10 flex-1 items-center justify-center gap-1.5 rounded border border-rule-strong bg-transparent px-3 py-1.5 font-display text-[13px] font-semibold text-ink transition-colors hover:bg-field sm:min-h-0 sm:flex-none sm:justify-start"
        >
          {startFreshEditAction.icon}
          {startFreshEditAction.label}
        </button>
      </>
    ) : null;

  // ---- v2 tool-rail relocation ----
  // The canvas's tool grid / color / stroke controls are relocated
  // out of the canvas's own column into the v2 right rail via the
  // canvas's `toolRailSlot` render-prop. The canvas hands us the
  // control block; we portal it into this rail target so it lands
  // in the rail's "Tools" section. State (not just a ref) so the
  // first portal render happens once the rail node mounts.
  const [toolRailTarget, setToolRailTarget] = useState<HTMLElement | null>(
    null,
  );
  const [formationControlsTarget, setFormationControlsTarget] =
    useState<HTMLElement | null>(null);

  // ---- v2 clip track block ----
  // Clip mode is active whenever the clip composer is the one in
  // play (create or edit) on the video surface. The clip track,
  // preset bar, and thumbnail strip render only then.
  const inClipMode = source === "video" && composerMode === "clip";
  // Clips on this match — the clip rail + clip-track lane data.
  // Primary source is the per-match query (`useCoachClips` →
  // `GET /api/coach/clips?match_id=`). The bundle's edit-mode seed
  // clip is unioned in so an edit target shows immediately on
  // edit-load (before the query settles) and so a clip not yet in
  // the freshest list page still renders; deduped by id, seed last
  // so the list copy wins once it lands.
  const matchClips = useMemo(() => {
    const fetched = coachClipsQuery.data?.clips ?? [];
    const seed =
      bundle.data?.clip && bundle.data.clip.match_id === matchId
        ? bundle.data.clip
        : null;
    if (!seed || fetched.some((c) => c.id === seed.id)) return fetched;
    return [...fetched, seed];
  }, [coachClipsQuery.data?.clips, bundle.data?.clip, matchId]);
  // The clip track + transport + preset bar + thumbnail strip,
  // rendered in the main content column below the video stage.
  const clipTrackBlock = (
    <div className="flex flex-col gap-3" data-testid="coach-review-clip-block">
      {/* Transport — frame stepper + live time readout, plus the
       * composer-drawer trigger buttons (the v2 layout opens the
       * note / clip composer on demand, not always-visible). */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <FrameStepper videoRef={videoElRef} />
        <span className="font-mono text-[11px] text-ink-secondary tabular-nums">
          {playhead.formatted} / {formatPlayhead(duration || null)}
        </span>
        {/* Composer triggers — hidden while a route-pinned edit is
         * open (the drawer is already shown for the edit) and while
         * the drawer itself is open. In clip mode the primary action
         * opens the clip details drawer; entering clip mode itself
         * keeps the drawer closed so the range controls stay usable. */}
        {!isNoteEdit && !isClipEdit && !composing ? (
          <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto">
            {inClipMode ? (
              <>
                <button
                  ref={composeButtonRef}
                  type="button"
                  onClick={openComposerClip}
                  data-testid="coach-review-add-clip-details"
                  className="inline-flex min-h-10 flex-1 items-center justify-center gap-1.5 rounded bg-accent px-3 py-1.5 font-display text-[13px] font-semibold text-accent-ink transition-colors hover:bg-accent-hover sm:min-h-0 sm:flex-none sm:justify-start"
                >
                  <FilmIcon size={14} />
                  Add details
                </button>
                <button
                  type="button"
                  onClick={exitClipMode}
                  data-testid="coach-review-exit-clip"
                  className="inline-flex min-h-10 flex-1 items-center justify-center rounded border border-rule-strong bg-transparent px-3 py-1.5 font-display text-[13px] font-semibold text-ink transition-colors hover:bg-field sm:min-h-0 sm:flex-none"
                >
                  Exit
                </button>
              </>
            ) : (
              <>
                <button
                  ref={composeButtonRef}
                  type="button"
                  onClick={openComposerNote}
                  data-testid="coach-review-mark-moment"
                  className="inline-flex min-h-10 flex-1 items-center justify-center gap-1.5 rounded border border-rule-strong bg-transparent px-3 py-1.5 font-display text-[13px] font-semibold text-ink transition-colors hover:bg-field sm:min-h-0 sm:flex-none sm:justify-start"
                >
                  <PlusIcon size={14} strokeWidth={2.5} />
                  Mark moment
                  <kbd className="rounded bg-field px-1 py-px font-mono text-[9px] text-ink-muted">
                    M
                  </kbd>
                </button>
                <button
                  type="button"
                  onClick={enterClipMode}
                  data-testid="coach-review-make-clip"
                  className="inline-flex min-h-10 flex-1 items-center justify-center gap-1.5 rounded bg-accent px-3 py-1.5 font-display text-[13px] font-semibold text-accent-ink transition-colors hover:bg-accent-hover sm:min-h-0 sm:flex-none sm:justify-start"
                >
                  <FilmIcon size={14} />
                  Make clip
                  <kbd className="rounded bg-accent-ink/20 px-1 py-px font-mono text-[9px]">
                    C
                  </kbd>
                </button>
              </>
            )}
          </div>
        ) : null}
        {(isNoteEdit || isClipEdit) && !composing ? (
          <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto">
            {renderEditActions()}
          </div>
        ) : null}
      </div>

      {/* Quick-clip preset bar — clip mode only, and only once the
       * video has a known duration (the presets clamp to it; the
       * thumbnail strip below samples it). With a placeholder /
       * unavailable video both are meaningless, so clip mode shows
       * just the composer + the inert track. */}
      {inClipMode && duration > 0 ? (
        <ClipPresetBar
          playhead={playhead.currentTime ?? 0}
          duration={duration}
          onSetWindow={applyClipWindow}
          onSetIn={setClipIn}
          onSetOut={setClipOut}
          onGoToIn={goToClipFirstFrame}
          onReset={resetClipWindowToPlayhead}
        />
      ) : null}

      {/* The scrub track — playhead, note marks, draggable IN/OUT. */}
      <ClipTrack
        duration={duration}
        playhead={playhead.currentTime ?? 0}
        onSeek={seekVideo}
        clipWindow={clipWindow}
        onClipWindowChange={applyClipWindow}
        inClipMode={inClipMode}
        notes={railNotes}
        clips={matchClips}
        activeNoteId={activeNoteId}
        onJumpToNote={handleSeekNote}
        loop={loop}
        onLoopChange={setLoop}
      />

      {/* Thumbnail strip — clip mode only, around the window, and
       * only with a known duration (it samples the span). */}
      {inClipMode && clipWindow && duration > 0 ? (
        <ThumbnailStrip
          duration={duration}
          window={clipWindow}
          onSeek={seekVideo}
        />
      ) : null}
    </div>
  );

  // Timecode line for the composer drawer's header — the clip
  // `IN → OUT · DUR` range in clip mode, otherwise the playhead the
  // note will save at.
  const composerTimecodeLabel =
    composerMode === "clip" && clipWindow
      ? `${formatPlayhead(clipWindow.start)} → ${formatPlayhead(
          clipWindow.end,
        )}`
      : `Min ${playhead.formatted}`;

  // The canvas's `toolRailSlot` implementation — portals the
  // canvas's own tool controls into the v2 right rail's Tools
  // section. Returns a portal (which renders into `toolRailTarget`),
  // so nothing shows at the canvas's location. Until the rail node
  // has mounted (`toolRailTarget` null on the very first paint) it
  // returns null and the controls simply aren't shown for that one
  // frame — the rail mounts in the same commit, so the next render
  // fills it.
  const renderToolRailSlot = (toolControls: React.ReactNode) =>
    toolRailTarget
      ? createPortal(<ToolRail>{toolControls}</ToolRail>, toolRailTarget)
      : null;
  const renderFormationControlsSlot = (formationControls: React.ReactNode) =>
    formationControlsTarget
      ? createPortal(formationControls, formationControlsTarget)
      : null;

  // ---- v2 top-row context controls ----
  // Video mode anchors notes/clips to a match + slot. The picker is
  // now rendered in the top control row beside the source toggle.
  // Tactical-board formation controls are portaled into that same
  // row by `TacticalBoardEditor` via `formationControlsSlot`.
  const railHeader =
    source === "video" ? (
      <div
        className="flex items-center gap-2"
        data-testid="coach-review-rail-header"
      >
        <MatchSlotPicker
          matches={matchesQuery.data ?? []}
          matchesLoading={matchesQuery.isLoading}
          matchId={matchId}
          onMatchIdChange={(id) => {
            setMatchId(id);
            if (id) setMatchError(null);
            // The admin-kill banner is cleared by the
            // `[matchId, slot]` effect — one source of truth.
          }}
          slot={slot}
          onSlotChange={setSlot}
          disabled={isClipEdit}
        />
        {/* Live timestamp readout — a new video note saves at the
         * player's current position. `--:--` until the video loads.
         * Note-create only. */}
        {!isNoteEdit && composerMode === "note" ? (
          <p
            className="shrink-0 whitespace-nowrap text-ink-muted text-xs"
            data-testid="coach-review-timestamp-readout"
          >
            Note saves at{" "}
            <span className="font-mono text-ink">{playhead.formatted}</span>
          </p>
        ) : null}
      </div>
    ) : null;

  const composerElement = (
    <div className="flex flex-col gap-4">
      {/* The match + slot picker, the live timestamp readout, and
       * the canvas-control rail all moved OUT of this drawer into
       * the v2 right rail (see `railHeader` below). The Save Note /
       * Save Clip toggle was removed entirely — the drawer's kind is
       * fixed by which trigger opened it ("Mark moment" → note,
       * "Make clip" → clip). The drawer now holds only the feedback
       * form. `matchError` is a save-time validation message, so it
       * stays here next to the composer. */}
      {source === "video" && matchError ? (
        <p
          className="text-destructive font-mono text-xs"
          data-testid="coach-review-match-error"
        >
          {matchError}
        </p>
      ) : null}
      {composerMode === "clip" && source === "video" ? (
        <>
          <ClipComposer
            ref={clipComposerRef}
            key={
              isClipEdit
                ? `edit-${bundle.data?.clip?.id ?? "loading"}-${clipComposerKey}`
                : `create-${clipComposerKey}`
            }
            defaultValues={
              // Precedence: a focus-mode snapshot wins (it carries
              // the coach's live edits across the portal remount);
              // then the edit-mode seed clip; then the frozen
              // create-mode window. The v2 clip track's live
              // `clipWindow` overrides start/end on top — the track
              // is the source of truth for the window, and the
              // composer drawer mounts fresh (key-bumped) each open
              // so a track drag never churns the composer's other
              // fields mid-edit.
              {
                ...(clipComposerOverride ??
                  clipEditDefaults ??
                  clipCreateDefaults ??
                  {}),
                ...(clipWindow
                  ? {
                      start_seconds: Number(clipWindow.start.toFixed(1)),
                      end_seconds: Number(clipWindow.end.toFixed(1)),
                    }
                  : {}),
              }
            }
            players={players}
            onSubmit={handleClipSubmit}
            submitLabel={
              saveClip.isError
                ? "Retry save"
                : isClipEdit
                  ? "Update clip"
                  : "Save clip"
            }
            matchLabel={
              selectedMatch ? matchLabel(selectedMatch) : "No match selected"
            }
            slot={slot}
            isEditing={isClipEdit}
            sourceNoteId={bundle.data?.clip?.source_note_id ?? null}
          />
          {/* Preview clip — opens the focused feedback player modal.
           * Only in clip-EDIT mode: the bundle's seed clip is a
           * persisted, fully-shaped clip to preview. (Create mode
           * has no saved clip yet — the coach saves first, then
           * the route flips to ?clip_id= and the button appears.) */}
          {isClipEdit && bundle.data?.clip && selectedMatch ? (
            <button
              type="button"
              onClick={() => setClipPreviewOpen(true)}
              data-testid="coach-review-preview-clip"
              className="rounded border border-rule bg-surface px-4 py-2 text-sm font-display uppercase tracking-wide text-ink hover:bg-field transition-colors"
            >
              Preview clip
            </button>
          ) : null}
        </>
      ) : (
        <>
          <TemplateSelector
            getCurrentValues={getLiveComposerValues}
            onApply={handleTemplateApply}
          />
          <Composer
            ref={composerRef}
            key={composerKey}
            defaultValues={composerOverride ?? composerDefaults}
            players={players}
            onSubmit={handleSubmit}
            submitLabel={submitLabel}
            observationMode={source === "tactical_board"}
            // M7.5 F: AI panel needs the active video match's id
            // for evidence. Empty for tactical-board / observation
            // composers (the panel handles `matchId === ""`).
            matchId={matchId}
          />
          {/* Preview note — opens the focused feedback player modal
           * in note mode (video + telestration replay). Only for a
           * VIDEO note being edited: the bundle's seed note is a
           * persisted, fully-shaped note with a match anchor.
           * Observations have no video to play. */}
          {isNoteEdit &&
          bundle.data?.note?.note_context === "video" &&
          selectedMatch ? (
            <button
              type="button"
              onClick={() => setNotePreviewOpen(true)}
              data-testid="coach-review-preview-note"
              className="rounded border border-rule bg-surface px-4 py-2 text-sm font-display uppercase tracking-wide text-ink hover:bg-field transition-colors"
            >
              Preview note
            </button>
          ) : null}
        </>
      )}
    </div>
  );

  const workspaceHeader = (
    <>
      {/* Constant workspace-level header — identical to the
       * `/coach` shell's, so the two routes share one chrome.
       * Coach Review is its own route, so it renders this itself
       * rather than nesting under the shell. */}
      <header className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row">
        <div className="min-w-0">
          <h1 className="text-2xl font-display tracking-tight sm:text-3xl">
            Coaching Workspace
          </h1>
          <p className="text-ink-muted text-sm mt-1">
            Manage your roster, write timestamped notes, save clips, and mark
            up video — all in one place.
          </p>
        </div>
        <div className="w-full sm:w-auto">
          <TeamScopeBar />
        </div>
      </header>

      {/* The shared coach-workspace tab strip — same as the shell's.
       * The strip's "Review" tab is the active one here. */}
      <div className="mb-4">
        <CoachTabStrip
          active="review"
          visibleTabs={
            me.data?.user && !canAuthorCoachFeedback(me.data)
              ? OPERATIONAL_COACH_TABS
              : undefined
          }
        />
      </div>
    </>
  );

  if (selectionRequired) {
    return (
      <main className="min-h-screen p-4 text-ink sm:p-6">
        {workspaceHeader}
        <TeamScopeGate />
      </main>
    );
  }

  return (
    <main className="min-h-screen p-4 text-ink sm:p-6">
      {workspaceHeader}

      {/* M9a A8 — auth gate. The shell-level <CoachReviewAuthGate>
       * effect (below) handles the redirect to `/login?next=/coach/review`.
       * Nothing renders in place of the old <InlineLoginPanel> — the
       * redirect happens before the user sees a flash of empty UI. */}
      <CoachReviewAuthGate bundle={bundle} />

      {bundle.isError &&
      !(bundle.error instanceof ApiError && bundle.error.status === 401) ? (
        <p
          className="text-destructive font-mono text-sm mb-4"
          data-testid="coach-review-bundle-error"
        >
          Failed to load bundle:{" "}
          {bundle.error instanceof Error ? bundle.error.message : "unknown"}
        </p>
      ) : null}
      {saveNote.isError ? (
        <p
          className="text-destructive font-mono text-sm mb-4"
          data-testid="coach-review-save-error"
        >
          Save failed:{" "}
          {saveNote.error instanceof Error
            ? saveNote.error.message
            : "unknown"}
        </p>
      ) : null}
      {streamKilled ? (
        <p
          className="text-destructive font-mono text-sm mb-4"
          data-testid="coach-review-stream-killed-error"
        >
          This stream was disconnected by an administrator. Pick a
          different match to resume.
        </p>
      ) : null}

      <div
        className="flex flex-col gap-4"
        data-testid="coach-review-shell"
        hidden={
          bundle.isError && bundle.error instanceof ApiError && bundle.error.status === 401
            ? true
            : undefined
        }
      >
        {/* Stage-control row — source toggle, source-specific
         * context controls, then surface actions. Match/slot and
         * tactical formation controls live here so they stay visible
         * even when the drawing tools are tucked into the mobile
         * slide-in panel. */}
        <div className="flex items-center gap-3 overflow-x-auto pb-1">
          <div className="flex min-w-max flex-1 items-center gap-3">
            <SourceToggle
              value={source}
              onChange={switchSource}
            />
            <div
              className="shrink-0"
              data-testid="coach-review-context-controls"
            >
              {source === "video" ? (
                railHeader
              ) : (
                <div ref={setFormationControlsTarget} />
              )}
            </div>
          </div>
          {/*
            #232 — the composer drawer at the bottom of the viewport is
            `fixed inset-x-0 bottom-0 z-50` (see ComposerDrawer.tsx:87).
            Its subtree intercepts pointer events over the top-right
            control row even though those buttons render visually above
            it. Lifting just this button cluster to `z-[60]` (above the
            drawer's z-50) restores clickability without raising the
            whole stage-control row above any other portals. The
            `relative` is required for `z-index` to apply to this
            non-positioned flex child. Reviewer-flagged on #232.

            This is the SOURCE of the click-blocking — not a focus-mode
            internal issue. `openFocus` already snapshots the in-progress
            composer override and closes the drawer (see comment block
            at ~L987), so the transition stays correct once the click
            actually lands.
          */}
          <div className="relative z-[60] flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setMobileToolsOpen((open) => !open)}
              aria-expanded={mobileToolsOpen}
              aria-controls="coach-review-drawing-tools"
              data-testid="coach-review-mobile-tools-toggle"
              className="rounded border border-rule bg-surface px-3 py-1.5 text-sm font-display uppercase tracking-wide text-ink transition-colors hover:bg-field lg:hidden"
            >
              Tools
            </button>
            <button
              ref={focusButtonRef}
              type="button"
              onClick={openFocus}
              data-testid="coach-review-focus-button"
              className="rounded border border-rule bg-surface px-3 py-1.5 text-sm font-display uppercase tracking-wide text-ink transition-colors hover:bg-field"
            >
              Focus mode
            </button>
            <button
              type="button"
              onClick={() => setHelpOpen((v) => !v)}
              aria-expanded={helpOpen}
              aria-label="Keyboard shortcuts"
              data-testid="coach-review-help-button"
              className="h-9 w-9 rounded border border-rule bg-surface font-mono text-ink-muted transition-colors hover:bg-field hover:text-ink"
            >
              ?
            </button>
          </div>
        </div>

        {/* v2 two-column workspace — the stage column (flex-1) and
         * the notes/clips rail (320px). The canvas's own tool /
         * composer rail still lives inside the canvas via
         * `toolbarChildren`; this NotesAndClipsRail is the v2
         * addition that splits the note + clip lists into their own
         * consistently-styled column. */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <section
          ref={stageMeasureRef}
          className="min-w-0 flex-1 overflow-hidden"
          aria-label="Stage"
          id="coach-review-source-panel"
          role="tabpanel"
          aria-labelledby={`coach-review-source-tab-${source}`}
        >
          {/* When focus mode is open, the inline stage is hidden —
           * the same canvas is mounted inside the fullscreen
           * overlay portal below. Two stage mounts would create
           * conflicting setSurface effects on the review-editor
           * store. The composer node is also rendered (via the
           * canvas's `toolbarChildren` slot below the tool grid),
           * so it stays alive inside the inline canvas — RHF state
           * survives focus-mode toggles. */}
          {!focusOpen ? (
            source === "tactical_board" ? (
              <div className="flex flex-col gap-3">
                <TacticalBoardEditor
                  scene={scene}
                  onSceneChange={setScene}
                  width={boardStageWidth}
                  toolRailSlot={renderToolRailSlot}
                  formationControlsSlot={renderFormationControlsSlot}
                  />
                {/* Observation details are explicit in edit mode so
                 * the board scene remains the first thing the coach
                 * sees. New observations still use Mark observation. */}
                {isNoteEdit && !composing ? (
                  <div className="flex flex-col gap-2">
                    <p
                      className="font-mono text-[11px] text-ink-muted"
                      data-testid="coach-review-observation-edit-hint"
                    >
                      The board shows this saved observation — edits update
                      it. New observation clears the board for a fresh one.
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      {renderEditActions()}
                    </div>
                  </div>
                ) : !isNoteEdit && !isClipEdit && !composing ? (
                  <div className="flex">
                    <button
                      ref={composeButtonRef}
                      type="button"
                      onClick={openComposerNote}
                      data-testid="coach-review-mark-observation"
                      className="inline-flex min-h-10 flex-1 items-center justify-center gap-1.5 rounded border border-rule-strong bg-transparent px-3 py-1.5 font-display text-[13px] font-semibold text-ink transition-colors hover:bg-field sm:min-h-0 sm:flex-none sm:justify-start"
                    >
                      <PlusIcon size={14} strokeWidth={2.5} />
                      Mark observation
                      <kbd className="rounded bg-field px-1 py-px font-mono text-[9px] text-ink-muted">
                        M
                      </kbd>
                    </button>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <TelestratorCanvas
                  drawing={drawing}
                  onDrawingChange={setDrawing}
                  width={videoStageWidth}
                  match={selectedMatch}
                  slot={slot}
                  videoRef={handleVideoElRef}
                  onHeartbeatKilled={() => setStreamKilled(true)}
                  toolRailSlot={renderToolRailSlot}
                  showDrawing={showVideoDrawing}
                />
                {/* v2 clip-track block — transport + scrub track +
                 * (clip mode) preset bar + thumbnail strip. Replaces
                 * the v1 TimelineChipRail: the note marks now live on
                 * the scrub track itself, and the right rail's
                 * NotesAndClipsRail carries the note list. */}
                {clipTrackBlock}
              </div>
            )
          ) : (
            <div
              className="rounded border border-rule bg-surface text-ink-muted text-sm font-mono px-4 py-3"
              data-testid="coach-review-stage-focused-placeholder"
            >
              Stage is open in focus mode. Press <kbd>Esc</kbd> or click Exit to return.
            </div>
          )}
        </section>

        {!focusOpen && mobileToolsOpen ? (
          <button
            type="button"
            aria-label="Close drawing tools"
            className="fixed inset-0 z-40 bg-canvas/70 backdrop-blur-sm lg:hidden"
            onClick={() => setMobileToolsOpen(false)}
          />
        ) : null}

        {/* Tools rail — v2 right column. Tools ONLY (the canvas
         * portals its toolbar in here via `toolRailSlot`); it sizes
         * to its content. The notes/clips lists used to live here
         * too but moved to a full-width horizontal-card block below
         * the canvas — a height-locked rail squeezed them into an
         * unusable nested scroll. Under `lg`, it becomes a slide-in
         * overlay so tools never push the video timeline down-page.
         * Hidden while focus mode owns the screen. */}
        {!focusOpen ? (
          <aside
            id="coach-review-drawing-tools"
            className={`fixed inset-y-0 right-0 z-50 flex w-[min(22rem,calc(100vw-1rem))] flex-col gap-3 overflow-y-auto border-l border-rule bg-surface p-3 shadow-pop transition-transform duration-200 ease-out lg:static lg:z-auto lg:max-h-none lg:w-80 lg:flex-shrink-0 lg:translate-x-0 lg:overflow-visible lg:rounded lg:border lg:p-4 lg:shadow-none ${mobileToolsOpen ? "visible translate-x-0" : "invisible translate-x-full pointer-events-none lg:visible lg:pointer-events-auto"}`}
            aria-label="Drawing tools"
          >
            <div className="flex items-center justify-between gap-3 lg:hidden">
              <p className="font-display text-xs font-semibold uppercase tracking-wide text-ink-muted">
                Drawing tools
              </p>
              <button
                type="button"
                onClick={() => setMobileToolsOpen(false)}
                className="rounded border border-rule bg-canvas px-2.5 py-1 text-xs font-display font-semibold uppercase tracking-wide text-ink transition-colors hover:bg-field"
              >
                Hide
              </button>
            </div>
            <div ref={setToolRailTarget} />
          </aside>
        ) : null}
        </div>

        {/* Context rail — video shows match-specific notes/clips;
         * tactical-board mode shows recent team observations instead
         * of a misleading match rail. */}
        {!focusOpen ? (
          source === "tactical_board" ? (
            <RecentObservationsRail
              observations={recentObservations}
              loading={bundle.isLoading}
              activeObservationId={isNoteEdit ? search.note_id : null}
              onOpenObservation={(note) => {
                void navigate({
                  to: "/coach/review",
                  search: { note_id: note.id, source: "tactical_board" },
                });
              }}
            />
          ) : (
            selectedMatch ? (
              <NotesAndClipsRail
                notes={railNotes}
                clips={matchClips}
                activeNoteId={activeNoteId}
                onJumpToNote={handleSeekNote}
                onOpenClip={openClipForEdit}
                notesLoading={coachNotesQuery.isLoading}
                clipsLoading={coachClipsQuery.isLoading}
              />
            ) : (
              <section
                className="rounded border border-rule bg-surface p-4"
                data-testid="coach-review-video-context-empty"
              >
                <h4 className="mb-2 font-mono text-[10px] uppercase tracking-mono text-ink-muted">
                  Match notes and clips
                </h4>
                <p className="flex h-[4.5rem] w-full items-center rounded border border-dashed border-rule px-3 font-mono text-xs text-ink-muted">
                  Select a match to see its notes and clips.
                </p>
              </section>
            )
          )
        ) : null}
      </div>

      {/* Composer drawer — the v2 bottom slide-up that hosts the
       * note / clip composer. Mounted only while `composing` is true
       * AND focus mode is closed (focus mode hosts the composer in
       * its own floating panel — see the FocusModeOverlay below — so
       * mounting it here too would double-mount the form). */}
      {composing && !focusOpen ? (
        <ComposerDrawer
          kind={composerMode}
          editing={isNoteEdit || isClipEdit}
          timecodeLabel={composerTimecodeLabel}
          onClose={closeComposer}
          // #366 — the drawer overlays the page's source tabs, but
          // flipping Video ↔ Tactical board mid-compose is a designed
          // flow (the composer relabels off the source). A compact
          // toggle in the drawer header keeps it mouse-reachable.
          // Distinct testids/attributes from the page SourceToggle so
          // strict-mode locators never see duplicates; the body
          // attribute mirror stays owned by the page toggle alone.
          headerSlot={
            <div
              role="tablist"
              aria-label="Composer source"
              data-testid="drawer-source-toggle"
              className="inline-flex rounded border border-rule bg-canvas p-0.5"
            >
              {(
                [
                  ["video", "Video"],
                  ["tactical_board", "Board"],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={source === value}
                  data-drawer-source={value}
                  onClick={() => switchSource(value)}
                  className={[
                    "rounded px-2 py-0.5 font-display text-[11px] uppercase tracking-wide transition-colors",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent",
                    source === value
                      ? "bg-accent text-accent-ink"
                      : "text-ink-muted hover:bg-field hover:text-ink",
                  ].join(" ")}
                >
                  {label}
                </button>
              ))}
            </div>
          }
        >
          {/* The drawer covers the board, which makes coaches afraid
           * to close it ("will I lose my details?") and blind to the
           * scene they're saving. A live thumbnail shows exactly
           * what rides along, and the caption says the round trip is
           * safe (closeComposer snapshots the form). */}
          {source === "tactical_board" ? (
            <div
              className="mb-3 flex items-center gap-3 rounded border border-rule bg-elevated p-2"
              data-testid="coach-review-drawer-board-preview"
            >
              <TacticalBoardThumbnail
                scene={scene}
                width={96}
                fallback="field"
                className="flex h-14 w-24 flex-shrink-0 items-center justify-center overflow-hidden rounded border border-rule bg-canvas"
              />
              <p className="font-mono text-[11px] text-ink-muted">
                This board saves with the observation. Close the drawer
                to keep editing it — the details typed here are kept.
              </p>
            </div>
          ) : null}
          {composerElement}
        </ComposerDrawer>
      ) : null}

      {/* Blank-wipe confirm — see the guard in handleSubmit. */}
      {pendingWipeValues ? (
        <ConfirmDialog
          title="Save with an empty board?"
          message="The board is empty, but this observation has a saved drawing. Updating now removes that drawing permanently."
          confirmLabel="Save empty board"
          danger
          onConfirm={() => {
            const values = pendingWipeValues;
            setPendingWipeValues(null);
            if (values) void submitNoteValues(values);
          }}
          onClose={() => setPendingWipeValues(null)}
          data-testid="coach-review-board-wipe-confirm"
        />
      ) : null}

      {focusOpen ? <FocusModeOverlay
        source={source}
        scene={scene}
        onSceneChange={setScene}
        drawing={drawing}
        onDrawingChange={setDrawing}
        match={selectedMatch}
        slot={slot}
        videoRef={handleVideoElRef}
        onHeartbeatKilled={() => setStreamKilled(true)}
        onClose={closeFocus}
        composer={composerElement}
        showDrawing={showVideoDrawing}
      /> : null}

      {/* Focused feedback player modal — M5 coach-side scope.
       * Previews the clip being edited. Renders only when the
       * bundle has a seed clip + a resolved match for its stream
       * URLs. */}
      {clipPreviewOpen && bundle.data?.clip && selectedMatch ? (
        <FeedbackPlayerModal
          target={{ kind: "clip", clip: bundle.data.clip }}
          match={selectedMatch}
          onClose={() => setClipPreviewOpen(false)}
        />
      ) : null}
      {notePreviewOpen && bundle.data?.note && selectedMatch ? (
        <FeedbackPlayerModal
          target={{ kind: "note", note: bundle.data.note }}
          match={selectedMatch}
          onClose={() => setNotePreviewOpen(false)}
        />
      ) : null}

      {/* M7.5 B7: source-aware shortcuts popover — swaps content
       * per active `source`. Ports the legacy
       * `_refreshCoachShortcutsHelpForSource` switch in
       * `js/coaching/review.js:798-833`. */}
      {helpOpen ? (
        <div
          role="dialog"
          aria-label="Keyboard shortcuts"
          data-testid="coach-review-help-popover"
          data-source={source}
          className="fixed bottom-4 right-4 z-50 max-w-sm rounded border border-rule bg-surface p-4 shadow-pop"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-display uppercase tracking-wider text-ink">
              Shortcuts · {source === "tactical_board" ? "Tactical board" : "Video"}
            </h3>
            <button
              type="button"
              onClick={() => setHelpOpen(false)}
              aria-label="Close shortcuts"
              className="text-ink-muted hover:text-ink text-xs"
            >
              ✕
            </button>
          </div>
          <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs font-mono">
            {(source === "tactical_board"
              ? TACTICAL_SHORTCUTS
              : VIDEO_SHORTCUTS
            ).map(([keys, label]) => (
              <ShortcutRow key={keys} keys={keys} label={label} />
            ))}
          </dl>
          <p className="text-xs text-ink-muted mt-3">
            Shortcuts skip while typing in a form field.
          </p>
        </div>
      ) : null}
    </main>
  );
}

// --- M7.5 B7: source-aware keyboard-shortcut tables ------------------
// Ports the legacy `_refreshCoachShortcutsHelpForSource` switch
// (`js/coaching/review.js:798-833`). Format: `[keysHtml, label]`
// pairs; `keysHtml` is a string with one or more keys separated by
// " / " (`ShortcutRow` splits + styles each).

const VIDEO_SHORTCUTS: ReadonlyArray<[string, string]> = [
  ["Space / K", "Play / pause"],
  ["J", "Back 5s"],
  ["← / →", "Back / forward 1s"],
  ["Shift+← / →", "Back / forward 10s"],
  [", / .", "Step back / forward 1 frame"],
  ["Shift+, / .", "Step back / forward 1 second"],
  ["C / M", "Clip mode / Mark moment"],
  ["I / O", "Set clip IN / OUT"],
  ["[", "Jump to clip first frame"],
  ["L", "Toggle loop in clip range"],
  ["F", "Toggle focus mode"],
  ["V / B", "Switch to Video / Board"],
  ["1 – 4", "Quick-pick tool"],
  ["A / Z / T", "Arrow / Zone / Label"],
  ["S / D / G", "Spotlight / Dim / Formation"],
  ["Esc", "Disarm / close"],
  ["?", "Toggle this help"],
];

const TACTICAL_SHORTCUTS: ReadonlyArray<[string, string]> = [
  ["F", "Toggle focus mode"],
  ["V / B", "Switch to Video / Board"],
  ["M", "Mark observation"],
  ["1 – 4", "Quick-pick tool"],
  ["A / Z / T", "Arrow / Zone / Label"],
  ["P / S / D / G", "Player / Spotlight / Dim / Formation"],
  ["Delete / Backspace", "Delete selected"],
  ["Esc", "Disarm + clear selection"],
  ["?", "Toggle this help"],
];

function ShortcutRow({ keys, label }: { keys: string; label: string }) {
  return (
    <>
      <dt className="text-ink-muted">{keys}</dt>
      <dd>{label}</dd>
    </>
  );
}

/**
 * Fullscreen focus-mode overlay. Renders the active canvas with
 * `hideToolbar` so the stage fills the viewport, plus the floating
 * tool overlay that the coach can reveal on demand.
 *
 * Mirrors the legacy Replay focus-mode UX: video / pitch maxes the
 * browser, the side composer is hidden, and tools appear as an
 * overlay only when the coach clicks the Tools pill.
 *
 * Portaled to `<body>` so the parent's bounded stacking context
 * (CSS `animation` on the route component creates one) can't trap
 * the overlay's z-index.
 *
 * Esc closes. The overlay tags itself `data-coach-review-portal` so
 * any future inert sweep (not currently active here — focus mode
 * doesn't need keyboard-trap because all interactable elements are
 * inside the overlay and the canvas pointer surface is the whole
 * viewport) would skip the portal container.
 */
function FocusModeOverlay(props: {
  source: CoachReviewSource;
  scene: BoardScene;
  onSceneChange: (s: BoardScene) => void;
  drawing: DrawingV2;
  onDrawingChange: (d: DrawingV2) => void;
  /** Resolved match + slot for the telestrator's `<HlsVideo>` —
   * `undefined` when no match is selected (the canvas then shows
   * the no-video placeholder). */
  match: Match | undefined;
  slot: string;
  /** Forwarded to the telestrator — the parent's callback ref that
   * captures the backing `<video>`, so the playhead readout /
   * keyboard scrubbing / save timestamp keep working while focus
   * mode owns the canvas. */
  videoRef: (el: HTMLVideoElement | null) => void;
  /** Forwarded to the telestrator — fires when the VOD heartbeat
   * gets a 403 (admin kill). */
  onHeartbeatKilled?: () => void;
  showDrawing: boolean;
  onClose: () => void;
  /** The composer stack — rendered inside the floating tool panel
   * below the drawing tools so focus mode is a full authoring
   * surface (draw + write), not draw-only. */
  composer: React.ReactNode;
}) {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const [overlaySize, setOverlaySize] = useState(() => ({
    width: typeof window === "undefined" ? 1200 : window.innerWidth,
    height: typeof window === "undefined" ? 800 : window.innerHeight,
  }));

  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setOverlaySize({
          width: Math.max(1, Math.floor(entry.contentRect.width)),
          height: Math.max(200, Math.floor(entry.contentRect.height)),
        });
      }
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Esc closes the overlay. Doesn't preventDefault — the broader
  // M4 keyboard-shortcut handler also reacts to Esc (disarms tools),
  // and both behaviors landing is fine.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") props.onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [props]);

  // The Tools toggle and its panel now live in the header /
  // anchored beneath the header — lift the open/close state up so
  // both stay in sync. Panel mount/unmount is preserved across
  // toggles (see FloatingToolPanel docstring).
  const [toolsOpen, setToolsOpen] = useState(false);

  // Largest 16:9 rectangle that fits the overlay (minus the header
  // chrome). Both surfaces are 16:9, so they share this width — the
  // video fills it; the tactical board is the 16:9 green field
  // (pitch markings drawn inset by `PitchBackground`).
  const CHROME_HEIGHT = 48;
  const VIDEO_ASPECT_W_OVER_H = 16 / 9;
  const availableH = Math.max(200, overlaySize.height - CHROME_HEIGHT);
  const availableW = Math.max(1, overlaySize.width);
  const stageW = Math.max(
    1,
    Math.min(availableW, Math.floor(availableH * VIDEO_ASPECT_W_OVER_H)),
  );

  if (typeof document === "undefined") return null;
  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[120] flex flex-col bg-black"
      data-testid="coach-review-focus-overlay"
      data-coach-review-portal="true"
      role="dialog"
      aria-modal="true"
      aria-label="Focus mode"
    >
      <header className="flex items-center justify-between border-b border-rule/40 bg-surface px-3 pb-2 pt-[calc(0.5rem_+_env(safe-area-inset-top))]">
        <span className="text-ink-muted text-[11px] font-display uppercase tracking-wider">
          Focus mode · {props.source === "tactical_board" ? "Tactical board" : "Video"}
        </span>
        <div className="flex items-center gap-2">
          <FloatingToolToggle
            surface={props.source}
            open={toolsOpen}
            onToggle={() => setToolsOpen((v) => !v)}
          />
          <button
            type="button"
            onClick={props.onClose}
            data-testid="coach-review-focus-exit"
            className="rounded border border-rule/60 bg-canvas px-2.5 py-1 text-xs font-display uppercase tracking-wide text-ink-muted transition-colors hover:bg-field hover:text-ink"
          >
            Exit focus
          </button>
        </div>
      </header>
      <div className="relative flex-1 flex items-center justify-center overflow-hidden">
        {props.source === "tactical_board" ? (
          <TacticalBoardEditor
            scene={props.scene}
            onSceneChange={props.onSceneChange}
            width={stageW}
            hideToolbar
          />
        ) : (
          <TelestratorCanvas
            drawing={props.drawing}
            onDrawingChange={props.onDrawingChange}
            width={stageW}
            match={props.match}
            slot={props.slot}
            videoRef={props.videoRef}
            onHeartbeatKilled={props.onHeartbeatKilled}
            hideToolbar
            showDrawing={props.showDrawing}
          />
        )}
        <FloatingToolPanel surface={props.source} open={toolsOpen}>
          {props.composer}
        </FloatingToolPanel>
      </div>
    </div>,
    document.body,
  );
}

/** M9a A8 — auth gate inside the Coach Review shell. Watches the
 * bundle query for 401; on auth failure, redirects to
 * `/login?next=/coach/review`. Also gates on role: a signed-in user
 * with no coach access who deep-links here is bounced to `/hub` (the
 * role-based redirect) rather than left on a shell whose queries
 * would 403 — and rather than sent to `/login`, which would loop an
 * already-authenticated user. Renders nothing; the effect fires
 * before React paints so the user doesn't see a flash of empty
 * shell. Ref-flag prevents the effect re-firing after the URL
 * updates (TanStack Router keeps the parent mounted briefly). */
function CoachReviewAuthGate({
  bundle,
}: {
  bundle: ReturnType<typeof useCoachReviewBundle>;
}) {
  const navigate = useNavigate({ from: "/coach/review" });
  const me = useMe();
  const isAuthError =
    bundle.error instanceof ApiError && bundle.error.status === 401;
  const isMeAuthError = me.error instanceof ApiError && me.error.status === 401;
  const isAnon = me.data === undefined ? false : me.data?.user == null;
  const showLogin = isAuthError || isMeAuthError || isAnon;

  // `next` captures the FULL path + search (`/coach/review?match_id=
  // …&note_id=…`) at mount via a ref so a coach deep-linked to a
  // specific match / note / clip resumes exactly there after signing
  // in, instead of an empty Coach Review surface. The ref freezes
  // the value before the redirect rewrites the URL; `window.location`
  // is the same source `PublicAppHeader` uses for its `loginNext`.
  const authGateMountPath = useRef(
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}` || "/coach/review"
      : "/coach/review",
  );
  const authRedirectFired = useRef(false);

  useEffect(() => {
    if (!showLogin) return;
    if (authRedirectFired.current) return;
    authRedirectFired.current = true;
    void navigate({
      to: "/login",
      search: { next: authGateMountPath.current },
      replace: true,
    });
  }, [showLogin, navigate]);

  // Wrong-role gate — mirrors CoachShell, but this route is a
  // feedback-authoring surface. Team admins can open the operational
  // Coach workspace, but direct /coach/review bookmarks redirect back
  // to Matches instead of rendering a feedback tool they cannot use.
  const canOpenCoach = getPublicNavCapabilities(me.data).canOpenCoach;
  const canOpenCoachReview = canAuthorCoachFeedback(me.data);
  const roleRedirectFired = useRef(false);
  useEffect(() => {
    if (showLogin) return;
    if (!me.data?.user) return;
    if (canOpenCoach && canOpenCoachReview) return;
    if (roleRedirectFired.current) return;
    roleRedirectFired.current = true;
    void navigate({
      to: canOpenCoach ? "/coach" : "/hub",
      search: canOpenCoach ? { tab: "matches" } : undefined,
      replace: true,
    });
  }, [showLogin, me.data, canOpenCoach, canOpenCoachReview, navigate]);

  return null;
}
